/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * QUẢN TRỊ — tài khoản · thời hạn · tuyến · tính năng. (`P-6` `B4`, đổi tên từ `Users.tsx`.)
 *
 * lee đặt hàng MỘT form, và **thứ tự các mục là một phần của yêu cầu**, không phải cách sắp
 * cho đẹp: *thông tin → hạng → thời hạn → tuyến → tính năng → đang dùng*. Đọc từ trên xuống là
 * đúng thứ tự người ta nghĩ khi mở một tài khoản cho khách: *ai · loại gì · tới bao giờ · ở
 * đâu · làm được gì · còn dùng không.*
 *
 * ⛔ MÀN HÌNH NÀY KHÔNG PHẢI CỬA QUYỀN. Mọi ô ở đây đều có một cửa THẬT ở máy chủ
 *   (`requireQuyen` · `tickThua` · `kepHanTheoCha` · phạm vi cây). Ẩn/khoá ở đây là để người
 *   bấm không phải học luật bằng cách ăn 403 — không phải để chặn ai.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ➕ `GD-01` 11/09/2026 — **ĐỔI VỎ SANG FLUENT UI v9** (Microsoft), dáng Windows 11.
 *
 * ⛔ ĐỔI VỎ NGHĨA LÀ: không một dòng LUẬT nào đổi. Không đổi lời gọi API, không đổi
 *   `useUsers`/`useRoutes`, không đổi `quyen.ts` · `caytaikhoan.ts` · `khongdau.ts`, không đổi
 *   thứ tự sáu mục của form, không đổi một câu thông báo lỗi nào. Mọi `useState`, mọi
 *   `useMemo`, mọi `useEffect`, mọi phép kẹp và mọi câu chú thích giải thích VÌ SAO đều ở
 *   nguyên chỗ cũ — chỉ những thẻ VẼ ra chúng là mới.
 *
 * 🔴 VÀ MÀU KHÔNG CÒN ĐƯỢC GÕ TAY. Cả tệp này không có một mã màu nào, không một lớp Tailwind
 *   màu nào: mọi màu đi qua `makeStyles` + `tokens.*` hoặc qua thuộc tính ngữ nghĩa của Fluent
 *   (`appearance="primary"`, `color="warning"`). Tailwind chỉ còn dựng **bố cục** (flex, gap,
 *   width, khoảng cách) — đúng ranh giới `GD-01` mục 4 vạch ra.
 *   ⇒ Hệ quả đo được: đổi `webLightTheme → webDarkTheme` ở `main.tsx` thì **cả trang** đổi màu.
 *     Chỗ nào đứng im là chỗ còn một mã màu gõ tay — phép phá của `GD-01` chính là phép ấy.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Badge, Button, Caption1, Card, CardHeader, Checkbox, Dialog, DialogActions, DialogBody,
  DialogContent, DialogSurface, DialogTitle, Dropdown, Field, Input, MessageBar, MessageBarActions, MessageBarBody,
  Option, OverlayDrawer, SearchBox, Spinner, Switch, Text, ToggleButton, Tree, TreeItem, TreeItemLayout,
  makeStyles, tokens,
} from '@fluentui/react-components';
import {
  CheckmarkCircle20Regular, Delete20Regular, Dismiss20Regular, Edit20Regular, Key20Regular, LockClosed16Regular,
  People20Regular, Person20Regular, PersonAdd20Regular, Power20Regular, Settings20Regular,
} from '@fluentui/react-icons';
import { api, errorMessage, ApiError } from '../lib/api';
import { qk, useMoPhong, useRoutes, useSucKhoeMayChu, useThongKeFirmware, useUsers } from '../lib/queries';
import { useAuth } from '../lib/auth';
/*
 * ⚠ `EmptyState` · `ErrorBox` · `useToast` GIỮ của `components/ui` (phiếu `GD-01` chốt): chúng
 *   không nằm trong "dáng Windows" mà phiếu này đo, và kéo chúng sang Fluent là lan ra ngoài
 *   phạm vi một phiếu — đúng thứ `GD-02`/`GD-03` sinh ra để làm, từng màn một.
 */
import { EmptyState, ErrorBox, useToast } from '../components/ui';
import { fmtAgo, fmtDate, fmtDateTime } from '../lib/format';
import { type Role, type RouteAccessItem, type UserRow } from '../lib/types';
import {
  GIAI_THICH_KHOA, GOI_HAN, HANG_HIEN, KHOA_TICK, NHAN_CO_TUYEN, NHAN_KHOA, TICK_MAC_DINH,
  cauTickKhongDanhChoHang, docHan, hanTheoGoi, tenHang, tickCamTheoHang, tickHopVoiHang,
} from '../lib/quyen';
import { khoaTim } from '../lib/khongdau';
import { CAU_HOI_BO_NHAP, useChanKeoTaiLai, useGiuKhiLui, useHoiKhiTaiLai } from '../lib/giunhap';
/*
 * ⚠ `GD-02b` — `Khoa` (khối bọc một ô) RỜI khỏi danh sách nhập: các ô tính năng bị khoá theo
 *   hạng nay gom vào MỘT `BangKhoa` thay vì mỗi ô một khối. `KhoiKhoa` vẫn còn — nó bọc cả một
 *   mục khi người dùng đang sửa CHÍNH MÌNH, một việc khác hẳn.
 */
import { BangKhoa, HuyHieuKhoa, KhoiKhoa } from '../components/Khoa';
import { gachNhanh, locThu, xepCay, type NutCay } from '../lib/caytaikhoan';

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * MÀU CỦA TRANG — `makeStyles` + `tokens`, và **KHÔNG một mã màu nào**.
 *
 * ⚠ Thuộc tính DÀI (`borderTopWidth`…) chứ không viết tắt: `makeStyles` chạy trên Griffel, và
 *   Griffel từng từ chối thuộc tính viết tắt. Bản mới nhận được; thuộc tính dài thì đúng ở MỌI
 *   bản — và tệp này không typecheck được trong hộp cát dev_app, nên tôi chọn cách không có
 *   cửa để sai.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
const useStyles = makeStyles({
  /*
   * ═══════════════════════════════════════════════════════════════════════════════════════
   * ⛔ `GD-02` — HAI LỚP `nguonBat`/`nguonTat` ĐÃ **XOÁ HẲN**, không phải để lại cho gọn mắt.
   *
   * 🔴 Chúng tô cho cái nút biểu tượng nguồn, mà `GD-02` thay nút ấy bằng `<Switch>` (mẫu lee
   *   duyệt). Để lại hai lớp không còn ai gọi thì trang có **HAI nguồn màu cho cùng một
   *   `isActive`**: một cái đang chạy (huy hiệu qua `mauTrangThai`) và một cái ngủ. Ngày ai đó
   *   cần tô lại trạng thái, họ sẽ gặp cái ngủ trước — nó nằm ngay đầu tệp, trông như thứ
   *   dành sẵn cho việc ấy — và hai chỗ lại lệch nhau đúng như trước.
   * ⇒ Luật *"một trạng thái, một màu"* được giữ bằng cách **chỉ còn một chỗ tô**, không phải
   *   bằng cách nhớ cho hai chỗ khớp nhau. Mục `[98]` đo đúng điều đó: hai cái tên này phải
   *   VẮNG MẶT khỏi tệp.
   * ═══════════════════════════════════════════════════════════════════════════════════════
   */

  /*
   * ⛔ NÚT NGUY HIỂM (xoá · vô hiệu hoá): Fluent v9 KHÔNG có `appearance="danger"`. Phiếu
   *   `GD-01` chốt sẵn cách làm: `Button` + `makeStyles` với `tokens.colorPaletteRedBackground3`.
   * ⚠ Chữ dùng `colorNeutralForegroundOnBrand` — chữ đặt trên một nền đậm, không phải chữ
   *   thường. Gõ `white` ở đây là gãy ngay khi đổi chủ đề.
   */
  nutNguy: {
    backgroundColor: tokens.colorPaletteRedBackground3,
    color: tokens.colorNeutralForegroundOnBrand,
    borderTopColor: tokens.colorPaletteRedBorder2,
    borderRightColor: tokens.colorPaletteRedBorder2,
    borderBottomColor: tokens.colorPaletteRedBorder2,
    borderLeftColor: tokens.colorPaletteRedBorder2,
    ':hover': {
      backgroundColor: tokens.colorPaletteRedBackground3,
      color: tokens.colorNeutralForegroundOnBrand,
    },
    ':hover:active': {
      backgroundColor: tokens.colorPaletteRedBackground3,
      color: tokens.colorNeutralForegroundOnBrand,
    },
  },

  /** Khối một MỤC của form — viền trung tính, nền nổi nhẹ. */
  muc: {
    borderTopWidth: tokens.strokeWidthThin,
    borderRightWidth: tokens.strokeWidthThin,
    borderBottomWidth: tokens.strokeWidthThin,
    borderLeftWidth: tokens.strokeWidthThin,
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    borderRadius: tokens.borderRadiusLarge,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
  },
  /** Số thứ tự của mục — thứ tự là một phần của yêu cầu, nên nó phải nhìn thấy được. */
  soMuc: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    borderRadius: tokens.borderRadiusCircular,
  },
  /** Câu giải thích PHẠM VI — trung tính, KHÔNG mượn màu hổ phách của `<Khoa>`. */
  ghiChu: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    borderRadius: tokens.borderRadiusMedium,
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
  },
  mo: { color: tokens.colorNeutralForeground3 },
  /** `GD-09` mục 7 — huy hiệu "N tài khoản phụ": nền ĐẶC để đọc được ở CẢ hai chế độ. */
  hhTaiKhoanPhu: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
  },
  /*
   * ➕ `GD-05` — TIÊU ĐỀ TRANG. Bản đầu tôi gõ `text-chu-1` / `text-chu-2` (lớp Tailwind của
   *   bảng cũ) và bộ kiểm đỏ đúng chỗ: trang này vẽ HOÀN TOÀN bằng Fluent, nên một lớp màu
   *   Tailwind sót lại là **hai hệ cùng tô một chữ** — và ngày `thietke.css` đổi thang màu,
   *   đúng hai dòng ấy đứng im trong khi cả trang đổi.
   * ⚠ `colorNeutralForeground1/2` KHÔNG phải màu của Fluent: `main.tsx` đã bắc cầu chúng về
   *   `--chu-1` / `--chu-2` của `thietke.css` (mục `[99]` đo cái cầu ấy). Nên đây vẫn là
   *   cùng một màu như trước, chỉ khác ở chỗ nó đi qua MỘT nguồn thay vì hai.
   */
  tieuDeTrang: { color: tokens.colorNeutralForeground1 },
  phuTieuDe: { color: tokens.colorNeutralForeground2 },
  /*
   * ➕ `GD-02` — TÍM = **tài khoản · thời hạn** (`--tim`, màu thứ sáu của `thietke.css`).
   * ⛔ Không mượn cam: "còn 19 ngày là hết hạn" không phải một việc cần làm NGAY, và dạy mắt
   *   rằng cam đôi khi không gấp là cách chắc nhất để cam thật sự gấp bị bỏ qua.
   */
  tim: { color: 'rgb(var(--tim))' },
  timNen: { backgroundColor: 'rgb(var(--tim) / 0.18)', color: 'rgb(var(--tim))' },
  /** Một hàng của cây tài khoản — bo 12 px, nền `nen-2`, đúng mẫu lee duyệt. */
  hangCay: {
    backgroundColor: 'rgb(var(--nen-2))',
    borderRadius: '12px',
    marginBottom: '8px',
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    paddingRight: tokens.spacingHorizontalS,
  },
  /*
   * ➕ `GD-05` — HÀNG ĐANG CHỌN ở danh sách. `--nen-3` là tầng "vật thể bấm được" của bảng
   *   màu `GD-07A`, và nó ĐẬM HƠN `hangCay` (`--nen-2`) đúng một bậc — vừa đủ để mắt tìm ra
   *   hàng đang xem mà không biến nó thành thứ nổi nhất màn hình.
   * ⛔ KHÔNG dùng màu nhấn (`--nhan`) cho cả hàng: xanh dương nghĩa là "đang chọn" ở mức MỘT Ô,
   *   tô cả một hàng bằng nó thì nó tranh chỗ với huy hiệu trạng thái ngay trên hàng ấy.
   */
  hangDangChon: {
    backgroundColor: 'rgb(var(--nen-3))',
    outlineWidth: tokens.strokeWidthThin,
    outlineStyle: 'solid',
    outlineColor: 'rgb(var(--nhan) / 0.4)',
  },
  /** Ảnh đại diện chữ cái đầu — màu theo HẠNG (mẫu `25`). */
  anhDaiDien: {
    display: 'inline-grid',
    placeItems: 'center',
    width: '26px',
    height: '26px',
    flexShrink: 0,
    borderRadius: tokens.borderRadiusCircular,
    fontSize: '12px',
    fontWeight: 600,
    color: 'rgb(var(--tren-nhan))',
  },
  /* ── TK-45 §6: bảng tài khoản + ngăn kéo (mẫu lee y04c / y04d) — màu CHỈ từ token ── */
  chu1: { color: tokens.colorNeutralForeground1 },
  chu2: { color: tokens.colorNeutralForeground2 },
  nhanChu: { color: 'rgb(var(--nhan))' },
  canhChu: { color: 'rgb(var(--canh))' },
  canhCham: { backgroundColor: 'rgb(var(--canh))' },
  totChu: { color: 'rgb(var(--tot))' },
  totCham: { backgroundColor: 'rgb(var(--tot))' },
  xamCham: { backgroundColor: tokens.colorNeutralForeground3 },
  the: {
    backgroundColor: tokens.colorNeutralBackground1, borderRadius: '12px', overflow: 'hidden',
    borderTopWidth: '1px', borderRightWidth: '1px', borderBottomWidth: '1px', borderLeftWidth: '1px',
    borderTopStyle: 'solid', borderRightStyle: 'solid', borderBottomStyle: 'solid', borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2, borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2, borderLeftColor: tokens.colorNeutralStroke2,
  },
  vienTren: { borderTopWidth: '1px', borderTopStyle: 'solid', borderTopColor: tokens.colorNeutralStroke2 },
  vienDuoi: { borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: tokens.colorNeutralStroke2 },
  dauBang: { backgroundColor: tokens.colorNeutralBackground2, color: tokens.colorNeutralForeground2 },
  hangBang: {
    cursor: 'pointer', borderTopWidth: '1px', borderTopStyle: 'solid', borderTopColor: tokens.colorNeutralStroke2,
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  soDem: {
    color: 'rgb(var(--nhan))', backgroundColor: 'rgb(var(--nhan) / 0.1)', borderRadius: '4px',
    borderTopWidth: '1px', borderRightWidth: '1px', borderBottomWidth: '1px', borderLeftWidth: '1px',
    borderTopStyle: 'solid', borderRightStyle: 'solid', borderBottomStyle: 'solid', borderLeftStyle: 'solid',
    borderTopColor: 'rgb(var(--nhan) / 0.4)', borderRightColor: 'rgb(var(--nhan) / 0.4)',
    borderBottomColor: 'rgb(var(--nhan) / 0.4)', borderLeftColor: 'rgb(var(--nhan) / 0.4)',
  },
  chipVien: {
    color: tokens.colorNeutralForeground2, borderRadius: '6px',
    borderTopWidth: '1px', borderRightWidth: '1px', borderBottomWidth: '1px', borderLeftWidth: '1px',
    borderTopStyle: 'solid', borderRightStyle: 'solid', borderBottomStyle: 'solid', borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke1, borderRightColor: tokens.colorNeutralStroke1,
    borderBottomColor: tokens.colorNeutralStroke1, borderLeftColor: tokens.colorNeutralStroke1,
  },
  oChon: {
    minHeight: '40px', borderRadius: '8px', paddingLeft: '12px', paddingRight: '12px', fontSize: '13px',
    backgroundColor: tokens.colorNeutralBackground3, color: tokens.colorNeutralForeground1,
    borderTopWidth: '1px', borderRightWidth: '1px', borderBottomWidth: '1px', borderLeftWidth: '1px',
    borderTopStyle: 'solid', borderRightStyle: 'solid', borderBottomStyle: 'solid', borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke1, borderRightColor: tokens.colorNeutralStroke1,
    borderBottomColor: tokens.colorNeutralStroke1, borderLeftColor: tokens.colorNeutralStroke1,
  },
  nutChu: { color: 'rgb(var(--nhan))', backgroundColor: 'transparent', borderTopWidth: 0, borderRightWidth: 0, borderBottomWidth: 0, borderLeftWidth: 0, cursor: 'pointer' },
  vaiSuper: { backgroundColor: 'rgb(var(--nhan) / 0.15)', color: 'rgb(var(--nhan))' },
  vaiManager: { backgroundColor: tokens.colorNeutralBackground3, color: tokens.colorNeutralForeground2 },
  vaiOperator: { backgroundColor: 'rgb(var(--nhan-dam) / 0.2)', color: 'rgb(var(--nhan-sang))' },
  vaiViewer: { backgroundColor: tokens.colorNeutralBackground4, color: tokens.colorNeutralForeground3 },
  ngan: { width: '480px', maxWidth: '100vw' },
  nhanNgan: { color: 'rgb(var(--nhan))', letterSpacing: '0.14em', textTransform: 'uppercase', fontSize: '11px', fontWeight: 600 },
  avSuper: { backgroundColor: 'rgb(var(--tim))' },
  avAdmin: { backgroundColor: 'rgb(var(--nhan))' },
  avManager: { backgroundColor: 'rgb(var(--tot))' },

  /** Ô số lớn của trang (mẫu `25`): biểu tượng trái · nhãn · số 22 px. */
  oSo: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '18px',
    borderTopWidth: tokens.strokeWidthThin,
    borderRightWidth: tokens.strokeWidthThin,
    borderBottomWidth: tokens.strokeWidthThin,
    borderLeftWidth: tokens.strokeWidthThin,
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
  },
  oSoIcon: {
    display: 'grid',
    placeItems: 'center',
    width: '44px',
    height: '44px',
    flexShrink: 0,
    borderRadius: tokens.borderRadiusLarge,
  },
  oNhan: { backgroundColor: 'rgb(var(--nhan) / 0.15)', color: 'rgb(var(--nhan))' },
  oTot: { backgroundColor: 'rgb(var(--tot) / 0.15)', color: 'rgb(var(--tot))' },
  oTim: { backgroundColor: 'rgb(var(--tim) / 0.18)', color: 'rgb(var(--tim))' },
  oCanh: { backgroundColor: 'rgb(var(--canh-nen))', color: 'rgb(var(--canh))' },

  /** Một hàng của bảng "Chi tiết & phân quyền". */
  hangCT: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    columnGap: tokens.spacingHorizontalM,
    borderBottomWidth: tokens.strokeWidthThin,
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    ':last-child': { borderBottomWidth: 0 },
  },

  hetHan: { color: tokens.colorPaletteRedForeground1 },
  hetHanGach: { color: tokens.colorPaletteRedForeground1, textDecorationLine: 'line-through' },
  canhBao: { color: tokens.colorPaletteYellowForeground1 },
  nhanh: { color: tokens.colorNeutralForeground4, fontFamily: tokens.fontFamilyMonospace },

  /*
   * ═══════════════════════════════════════════════════════════════════════════════════════
   * ➕ `GD-02b` — HỘP THOẠI TÀI KHOẢN: 960 px · 92 vh · **chỉ phần giữa cuộn**.
   *
   * 🔴 BA KHỐI DƯỚI ĐÂY LÀ MỘT BỘ BA, KHÔNG PHẢI BA LỰA CHỌN RỜI. Muốn "chân hộp luôn thấy"
   *   thì cả ba phải đúng cùng lúc:
   *       `hopTo`    đặt TRẦN chiều cao       — không có trần thì chẳng có gì để cuộn
   *       `thanHop`  cột dọc + `minHeight:0`  — thiếu `minHeight:0` thì con **không co lại**
   *                                             được (mặc định `min-height:auto` của flex),
   *                                             hộp cứ dài ra và trần trên thành vô nghĩa
   *       `vungCuon` chỗ DUY NHẤT `overflow-y:auto`
   *   Bỏ `minHeight: 0` là cái bẫy kinh điển của flexbox: mọi thứ trông đúng cho tới khi nội
   *   dung dài hơn màn hình, và lúc ấy chân hộp trôi xuống dưới mép — đúng chỗ lee vừa chỉ.
   *
   * ⚠ `maxWidth: '96vw'` đi kèm `width: '960px'`: trên màn 1366 px thì 960 px vẫn vừa, nhưng
   *   một cửa sổ hẹp hơn 960 px sẽ bị hộp đẩy ra ngoài mép nếu không có trần theo màn hình.
   * ═══════════════════════════════════════════════════════════════════════════════════════
   */
  /*
   * ⚠ `GD-02c` — `min(960px, calc(100vw - 32px))` thay cho cặp `width` + `maxWidth: 96vw`.
   *   `96vw` chừa 2% mỗi bên, tức khoảng chừa CO LẠI đúng lúc màn hẹp nhất — chỗ cần nó nhất.
   *   `calc(100vw - 32px)` chừa một con số CỐ ĐỊNH ở mọi bề ngang, đúng thứ một cái mép cần.
   */
  /*
   * 🚨 `GD-02d` — `maxWidth` LÀ VẾ BẮT BUỘC, và ảnh tôi tự chụp mới lòi ra điều đó.
   *
   * `DialogSurface` của Fluent mang sẵn `max-width: 600px`. `GD-02c` bỏ `maxWidth: '96vw'` đi
   * (vì muốn đổi cách chừa mép) và **không thay bằng gì** — nên từ `GD-02c` tới giờ, cái hộp
   * bị Fluent kẹp ở **600 px** ở MỌI màn hình, dù `width` viết 960. `width` một mình không
   * thắng nổi một `max-width` chặt hơn: đó là luật CSS, không phải một lỗi của Fluent.
   *
   * 🔴 ĐÂY LÀ THỨ CHỈ ẢNH MỚI THẤY. Phép canh `[109]` đọc thấy chuỗi `min(960px, …)` trong
   *   nguồn và báo XANH; `tsc` xanh; bộ phá xanh. Cả ba đều đo **CÁI TÔI VIẾT**, không đo
   *   **CÁI TRÌNH DUYỆT LÀM**. Ảnh lee gửi ("hộp ~665 px") là phép đo duy nhất chạm tới sự
   *   thật ấy — và L-TỰ-CHỤP sinh ra để tôi có phép đo đó TRƯỚC khi đẩy, chứ không phải sau.
   */
  hopTo: {
    width: 'min(960px, calc(100vw - 32px))',
    maxWidth: 'min(960px, calc(100vw - 32px))',
    maxHeight: '92vh',
  },
  thanHop: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '92vh',
    minHeight: 0,
  },
  vungCuon: {
    overflowY: 'auto',
    /* ⛔ `hidden` chứ không `auto`: một thanh cuộn NGANG ở đây là đúng thứ lee báo bị cắt chữ. */
    overflowX: 'hidden',
    minHeight: 0,
    flexGrow: 1,
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
  },

  /*
   * HAI CỘT — trái: thông tin + hạng/thời hạn/đang dùng · phải: tuyến + tính năng.
   * ⚠ Dưới 900 px XẾP MỘT CỘT, và đường kẻ dọc phải chuyển thành đường kẻ NGANG — một viền
   *   trái còn sót khi đã xếp dọc trông như một lỗi vẽ, không như một ranh giới.
   */
  /*
   * ⚠ `GD-02c` — NGƯỠNG MỘT CỘT 900 → **1000 px** (phiếu chốt), và lý do đo được: hộp rộng
   *   960 px, cột trái là `1fr` của `1fr 1.15fr` ⇒ **~430 px**. Ở 430 px thì ba ô nhập một
   *   hàng chen nhau tới mức nhãn dính vào nhau — đúng ảnh lee chụp ở cửa sổ ~1100 px.
   * 🔴 VÀ ĐÂY LÀ CHỖ DẠY LẠI MỘT ĐIỀU: **`@media` đo CỬA SỔ, không đo CÁI CỘT.** Ở 1100 px cửa
   *   sổ vẫn "rộng", nên mọi ngưỡng theo cửa sổ đều nói *"cứ xếp nhiều cột đi"* — trong khi cái
   *   cột thật chỉ còn 430 px. Ngưỡng 1000 px là cách nói *"cửa sổ hẹp cỡ này thì cột trái chắc
   *   chắn đã quá hẹp"*: một phép SUY từ bề ngang cửa sổ, không phải một phép ĐO cái cột.
   * ⛔ Nên cái lưới bên trong cột trái KHÔNG được dựa vào ngưỡng này để tự cứu: nó phải xếp
   *   sao cho 430 px vẫn đọc được (xem khối `luoiThongTin` dưới).
   */
  /*
   * ⛔ `GD-02d` — KHÔNG CÒN MỘT `@media` NÀO TRONG BẢNG KIỂU CỦA HỘP NÀY. Griffel không sinh
   *   ra chúng (ảnh lee 21:10: hộp 665 px vẫn hai cột), nên mỗi `@media` ở đây là một lời hứa
   *   không ai giữ — và tệ hơn một lời hứa thiếu, vì phép canh đọc thấy nó và báo XANH.
   * ⇒ Mỗi bố cục có HAI lớp: lớp thường và lớp `…Hep`, và `hep` (đo bằng `ResizeObserver`)
   *   chọn giữa chúng. Một phép chọn trong JavaScript thì chạy hoặc không chạy — không có
   *   khoảng giữa để nó im lặng biến mất.
   */
  haiCot: { display: 'grid', gridTemplateColumns: '1fr 1.15fr' },
  haiCotHep: { display: 'grid', gridTemplateColumns: '1fr' },
  cotTrai: { paddingRight: tokens.spacingHorizontalL, minWidth: 0 },
  cotTraiHep: { paddingRight: 0, minWidth: 0 },
  cotPhai: {
    paddingLeft: tokens.spacingHorizontalL,
    minWidth: 0,
    borderLeftWidth: tokens.strokeWidthThin,
    borderLeftStyle: 'solid',
    borderLeftColor: tokens.colorNeutralStroke2,
  },
  /*
   * ⚠ Xếp dọc rồi thì đường kẻ phải ĐỔI CHIỀU, không chỉ biến mất: một viền TRÁI còn sót khi
   *   đã xếp dọc nằm cạnh chữ như một lỗi vẽ; viền TRÊN mới còn là một ranh giới.
   */
  cotPhaiHep: {
    paddingLeft: 0,
    minWidth: 0,
    paddingTop: tokens.spacingVerticalL,
    marginTop: tokens.spacingVerticalL,
    borderTopWidth: tokens.strokeWidthThin,
    borderTopStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
  },

  /*
   * ═══════════════════════════════════════════════════════════════════════════════════════
   * ➕ `GD-02c` — LƯỚI KHỐI 1 (Họ tên · Email · SĐT): **HAI HÀNG × HAI Ô**, không ba ô một hàng.
   *
   * 🔴 LỖI 1 CỦA lee, và gốc của nó đáng ghi hơn cái sửa: lưới cũ viết bằng Tailwind
   *   `grid-cols-1 sm:grid-cols-3`. `sm:` là **640 px CỦA CỬA SỔ**. Ở ảnh lee chụp, cửa sổ là
   *   1100 px nên `sm:` bật — nhưng cái CỘT chứa lưới chỉ có ~430 px, và ba ô chia nhau 430 px
   *   thì mỗi ô còn ~130 px: nhãn "Họ và tên" / "Email" / "Số điện thoại" dính thành một dòng,
   *   ô nhập cắt cụt `khach_hang_11`.
   *   ⇒ **Tôi đo cửa sổ trong khi câu hỏi là về cái cột.** Cùng một họ với mọi lần "đo cái dây
   *     thay vì đo cái cổng" đã cắt ở bộ kiểm — lần này ở CSS.
   * ⇒ Hai cột thì mỗi ô ~205 px ở đúng cột 430 px ấy: đủ cho nhãn một dòng và cho giá trị dài
   *   nhất đang có. Ba ô một hàng KHÔNG cứu được bằng cách hạ ngưỡng, vì ngưỡng nào cũng là
   *   ngưỡng của cửa sổ.
   *
   * ⛔ `minWidth: 0` TRÊN MỌI Ô CON — đây là vế thứ hai, và thiếu nó thì hai cột cũng tràn:
   *   một con grid mặc định `min-width: auto` ⇒ **không hẹp hơn nội dung**. `Input` của Fluent
   *   có bề rộng mặc định riêng, nên nó sẽ đẩy cột rộng ra thay vì tự co. Hai vế đi cùng nhau
   *   hoặc không vế nào chạy.
   * ═══════════════════════════════════════════════════════════════════════════════════════
   */
  luoiThongTin: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: tokens.spacingHorizontalM,
    rowGap: tokens.spacingVerticalM,
    '> *': { minWidth: 0 },
  },
  luoiThongTinHep: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    rowGap: tokens.spacingVerticalM,
    '> *': { minWidth: 0 },
  },
  /** Ô chiếm cả hai cột của `luoiThongTin` (Mật khẩu tạm). */
  oCaHang: { gridColumnStart: 1, gridColumnEnd: -1, minWidth: 0 },
  /** Mọi `Input` trong hộp: bề rộng theo Ô CHỨA nó, không theo bề rộng mặc định của Fluent. */
  oNhapDay: { width: '100%', minWidth: 0 },

  /*
   * ═══════════════════════════════════════════════════════════════════════════════════════
   * Một DÒNG của khối "hạng · thời hạn · đang dùng" — phần chữ trái, điều khiển phải.
   *
   * ➕ `GD-02c` LỖI 2 & 4: *"Hạng: Vận hànhKhông đổi được…"* — tiêu đề và câu giải thích chạy
   *   liền thành một đoạn.
   * 🔴 GỐC: câu giải thích là `<Caption1 className="... block">`, và `block` là một lớp
   *   **Tailwind**. `Caption1` của Fluent mang lớp Griffel đặt `display` của riêng nó, và
   *   Griffel chèn CSS của mình SAU Tailwind ⇒ `block` thua, thẻ ở lại `inline`, hai câu dính
   *   nhau. Trang này vẽ hoàn toàn bằng Fluent, nên **một lớp Tailwind ở đây là một lớp sẽ
   *   thua** — đúng lý do luật `GD-05` cấm lớp màu Tailwind trong tệp này, chỉ khác thuộc tính.
   * ⇒ Bố cục của dòng do `makeStyles` lo hết: `chuDong` là một cột dọc, nên tiêu đề và câu
   *   giải thích là HAI dòng vì CẤU TẠO, không vì một lớp phải thắng ai.
   *
   * ⛔ `alignItems: 'flex-start'` chứ không `center`: câu giải thích có thể dài hai ba dòng, và
   *   căn giữa theo chiều dọc sẽ đẩy cái `Dropdown` xuống lửng lơ giữa khối chữ.
   * ⛔ `dkDong` `flexShrink: 0` — phần điều khiển KHÔNG co; phần chữ (`flexGrow: 1; minWidth: 0`)
   *   mới là chỗ nhường. Thiếu `minWidth: 0` thì chữ không xuống dòng và nó đẩy `Dropdown` ra
   *   ngoài mép.
   * ═══════════════════════════════════════════════════════════════════════════════════════
   */
  /*
   * ═══════════════════════════════════════════════════════════════════════════════════════
   * ➕ `GD-02d` mục 3 — BA DÒNG KHỐI 2 XẾP **DỌC**: tiêu đề → giải thích → điều khiển.
   *
   * 🔴 `GD-02c` đặt điều khiển CẠNH chữ và ép ô chọn Thời hạn xuống 150 px. Ảnh lee 21:10 cho
   *   thấy vì sao vẫn hỏng: ở hộp 665 px, cột trái còn ~280 px, trừ 150 px cho ô chọn và đệm
   *   hai bên thì chữ còn ~40 px — và 40 px thì mọi câu đều rớt từng chữ một dòng.
   * ⛔ SỬA BẰNG CẤU TẠO, KHÔNG BẰNG MỘT CON SỐ NHỎ HƠN: hạ 150 xuống 120 rồi 100 chỉ là dời
   *   cái ngưỡng vỡ, không bỏ nó — vì cái sai là **hai thứ tranh nhau MỘT hàng ngang**. Xếp
   *   dọc thì cả chữ lẫn điều khiển đều được trọn bề ngang, ở MỌI bề rộng hộp, và không còn
   *   một con số nào phải đoán.
   * ⚠ Giá phải trả: mỗi dòng cao thêm ~26 px. Chấp nhận — hộp đã cuộn dọc được, và một dòng
   *   cao hơn thì đọc được, còn một dòng rớt từng chữ thì không.
   * ═══════════════════════════════════════════════════════════════════════════════════════
   */
  dong: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    rowGap: tokens.spacingVerticalXS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
  },
  /** Phần CHỮ của một dòng: tiêu đề trên, giải thích dưới — hai dòng do CẤU TẠO. */
  chuDong: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '2px',
    flexGrow: 1,
    minWidth: 0,
  },
  tieuDeDong: {
    display: 'block',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  giaiThichDong: {
    display: 'block',
    fontSize: '12px',
    lineHeight: '16px',
    color: tokens.colorNeutralForeground2,
    overflowWrap: 'anywhere',
  },
  /*
   * Phần ĐIỀU KHIỂN của một dòng — nay nằm Ở DÒNG DƯỚI chữ, căn TRÁI.
   * ⚠ `alignSelf: 'flex-start'` giữ cho ổ khoá và `Switch` không bị kéo giãn hết bề ngang bởi
   *   `alignItems: 'stretch'` của dòng. Ô chọn Thời hạn thì NGƯỢC LẠI — nó muốn trọn bề ngang,
   *   nên nó có lớp riêng (`oChonHan`).
   */
  dkDong: { flexShrink: 0, alignSelf: 'flex-start' },
  /*
   * ⚠ `GD-02c` LỖI 3 — Ô CHỌN THỜI HẠN **150 px**, không 280.
   *   `Dropdown` của Fluent có `min-width` mặc định ~250–280 px. Trong một dòng rộng ~400 px,
   *   nó ăn 280 và để lại ~60 px cho chữ ⇒ câu giải thích rớt thành bảy dòng một chữ
   *   (*"Trống / = vĩnh / viễn…"*). Nhãn dài nhất thật sự phải hiện là "Ngày tuỳ ý" /
   *   "Giữ nguyên" — 150 px thừa sức.
   * ⛔ Phải đặt CẢ `minWidth` lẫn `maxWidth`: chỉ đặt `width` thì `min-width` mặc định của
   *   Fluent vẫn thắng và ô không hẹp lại được.
   */
  /*
   * ⚠ `GD-02d` — Ô CHỌN THỜI HẠN **100 %**, không 150 px. Nó nay đứng một mình ở dòng dưới,
   *   nên nó không tranh chỗ với ai: cho nó trọn bề ngang thì nhãn dài nhất ("Ngày tuỳ ý")
   *   luôn đọc được ở mọi bề rộng hộp, và không còn một con số cứng nào để đoán sai.
   * ⛔ `minWidth: 0` là vế bắt buộc: `Dropdown` của Fluent có `min-width` mặc định ~250 px, và
   *   nếu không hạ nó xuống thì ở hộp hẹp ô này vẫn đẩy cột rộng ra — đúng lỗi cũ, chỗ mới.
   */
  oChonHan: { width: '100%', minWidth: 0, maxWidth: '100%' },

  /*
   * BẢNG TUYẾN — `table-layout: fixed`, và đó là điều kiện CẦN cho "không cuộn ngang".
   * ⛔ Với `auto` (mặc định), một tên tuyến dài **đẩy rộng cả bảng** và `text-overflow` không
   *   bao giờ chạy — ô không bao giờ hẹp hơn nội dung của nó. `fixed` đảo lại: bề rộng do cột
   *   quyết định, nội dung phải tự cắt. Hai thứ này đi cùng nhau hoặc không thứ nào chạy.
   */
  /*
   * ═══════════════════════════════════════════════════════════════════════════════════════
   * ➕ `GD-02d` — BẢNG TUYẾN THÔI LÀ `<Table>` CỦA FLUENT, NÓ THÀNH MỘT **LƯỚI**.
   *
   * 🔴 Ảnh lee 21:10: cột tên mất hẳn, tiêu đề "Tuyến" đè lên "Tuyến", mỗi hàng cao ~340 px —
   *   chữ rớt từng KÝ TỰ một dòng. Ba thứ cộng lại gây ra nó, và không thứ nào sai một mình:
   *     · `table-layout: fixed` + ba cột tick **76 px CỨNG** trong một cột phải chỉ ~300 px
   *       ⇒ 228 px đã bị lấy, cột tên còn ~0;
   *     · `overflowWrap: 'anywhere'` (thêm ở `GD-02c` cho BĂNG CAM) chạm tới cả ô tên
   *       ⇒ ô rộng 0 px thì "anywhere" ngắt sau MỖI ký tự — đúng cái ảnh;
   *     · `ellipsis` không bao giờ chạy, vì nó chỉ chạy khi chữ KHÔNG xuống dòng.
   * ⛔ BÀI HỌC: `anywhere` là thuốc cho một CÂU, và là thuốc độc cho một CÁI TÊN. Một cái tên
   *   bị ngắt giữa chừng thì không còn là cái tên ấy nữa — mà đây là bảng PHÂN QUYỀN, nơi đọc
   *   nhầm một tên là trao nhầm quyền điều khiển một tuyến đường khác.
   * ⇒ `minmax(0, 1fr)` cho cột tên: **`0` là vế `min`**, tức là ô được phép hẹp lại tuỳ ý, và
   *   `nowrap` + `ellipsis` mới có chỗ chạy. Ba cột tick là con số cố định (64 px, hoặc 52 px
   *   khi hộp hẹp) — chúng chứa một ô tick, chúng không cần co.
   * ═══════════════════════════════════════════════════════════════════════════════════════
   */
  hangTuyen: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 64px 64px 64px',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS,
    paddingTop: tokens.spacingVerticalXXS,
    paddingBottom: tokens.spacingVerticalXXS,
    borderBottomWidth: tokens.strokeWidthThin,
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
  },
  hangTuyenHep: { gridTemplateColumns: 'minmax(0, 1fr) 52px 52px 52px' },
  /** Hàng TIÊU ĐỀ — cùng một lưới, nên các cột không thể lệch nhau. */
  dauTuyen: {
    color: tokens.colorNeutralForeground2,
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: tokens.fontWeightSemibold,
    paddingBottom: tokens.spacingVerticalXS,
  },
  oTick: { textAlign: 'center', minWidth: 0 },
  /* Hai mảnh chữ của ô tên — `<span>` thường, để `nowrap` của ô cha không bị con ghi đè. */
  tenTuyenDam: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1 },
  tenTuyenPhu: { fontSize: '12px', color: tokens.colorNeutralForeground3 },
  cotTen: { minWidth: 0 },
  /** Tên tuyến MỘT DÒNG, cắt "…" — `title` mang tên đầy đủ (xem chỗ vẽ). */
  /*
   * ⛔ `GD-02d` mục 5 — Ô TÊN **KHÔNG** ĐƯỢC `overflowWrap`. `nowrap` + `ellipsis` + `title`
   *   là một cơ chế ba mảnh: chữ không xuống dòng ⇒ tràn ⇒ bị cắt bằng "…" ⇒ đọc lại bằng
   *   `title`. Thêm `anywhere` vào là phá mảnh thứ nhất, và cả cơ chế đổ: chữ xuống dòng nên
   *   không còn gì để cắt, `ellipsis` chết, và ở một ô hẹp thì nó ngắt sau mỗi ký tự.
   */
  tenMotDong: {
    display: 'block',
    whiteSpace: 'nowrap',
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
  cotTick: { width: '76px', textAlign: 'center' },
  /*
   * ⚠ `GD-02d` — `cotTickDau` (lớp riêng cho ô tiêu đề của `<Table>` Fluent) ĐÃ BỎ cùng với
   *   chính cái `<Table>` ấy. Bảng tuyến nay là một LƯỚI (`hangTuyen`), và hàng tiêu đề dùng
   *   CHÍNH cái lưới đó — nên không còn hai bề ngang nào để lệch nhau, và không còn một cái
   *   `<button>` `overflow:hidden` nào của Fluent để nuốt mất nhãn cột.
   *   Xem `hangTuyen` / `dauTuyen` ở trên.
   */

  /*
   * Ô BỊ MÁY CHỦ TỪ CHỐI — viền đỏ. Đây là chỗ DUY NHẤT trang này dùng màu đỏ cho một ô nhập,
   * và nó hợp luật `[95]`: đỏ = **một sự cố đang xảy ra** (lệnh vừa bị từ chối), không phải
   * "cần chú ý sau". Băng ở chân hộp nói CÂU, ô này nói CHỖ.
   */
  oSai: {
    borderRadius: tokens.borderRadiusSmall,
    outlineWidth: tokens.strokeWidthThick,
    outlineStyle: 'solid',
    outlineColor: tokens.colorPaletteRedBorder2,
    backgroundColor: tokens.colorPaletteRedBackground1,
  },

  /** Tính năng: lưới 2 cột, mỗi ô MỘT DÒNG (mẫu `22`). */
  luoiTinhNang: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: tokens.spacingHorizontalL,
    rowGap: tokens.spacingVerticalXS,
  },
  luoiTinhNangHep: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    rowGap: tokens.spacingVerticalXS,
  },

  /*
   * CHÂN HỘP CỐ ĐỊNH: [băng lỗi chiếm hết bề ngang còn lại] [Huỷ] [Lưu].
   * 🔴 `flexShrink: 0` là thứ giữ lời hứa "luôn thấy": không có nó, chân hộp là một con flex
   *   bình thường và nó sẽ **co lại rồi biến mất** trước khi vùng cuộn chịu nhường chỗ.
   */
  chanHop: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalM,
    flexShrink: 0,
    flexWrap: 'wrap',
    rowGap: tokens.spacingVerticalS,
  },
  /*
   * ⛔ `minWidth: 0` TRÊN BĂNG LỖI — đây chính là chỗ lee báo *"bị cắt ngang"*. Một con flex
   *   mặc định `min-width: auto`, tức là **không hẹp hơn nội dung của nó**; câu lỗi dài đẩy
   *   chân hộp rộng ra, và chữ bị cắt ở mép. `minWidth: 0` cho nó co lại để chữ XUỐNG DÒNG.
   */
  bangLoi: { flexGrow: 1, flexBasis: '320px', minWidth: 0 },
  loiXuongDong: { whiteSpace: 'normal', overflowX: 'visible', wordBreak: 'break-word' },
});

/**
 * Màu của TRẠNG THÁI tài khoản — một hàm, hai chỗ đọc (huy hiệu + biểu tượng nguồn).
 *
 * ⚠ Vì sao HỔ PHÁCH chứ không ĐỎ cho "Đã khoá": dự án đã cắt một luật màu ở mục `[95]` —
 *   **đỏ = sự cố đang chạy** (tủ mất kết nối), **hổ phách = có gì đó cần người xử lý**. Một
 *   tài khoản bị khoá không phải sự cố: nó là trạng thái **ai đó cố ý đặt**.
 */
const mauTrangThai = (dangDung: boolean): 'success' | 'warning' => (dangDung ? 'success' : 'warning');

export default function QuanTriPage() {
  const s = useStyles();
  const { user: me } = useAuth();
  const users = useUsers(true);
  const toast = useToast();
  const qc = useQueryClient();

  const [editing, setEditing] = useState<UserRow | 'new' | null>(null);
  const [deleting, setDeleting] = useState<UserRow | null>(null);
  /** 409: tài khoản còn con — máy chủ trả về TÊN từng con, và màn hình phải bày ra. */
  const [conChanXoa, setConChanXoa] = useState<{ ten: string; con: string[] } | null>(null);
  /*
   * ➕ 10/09 tối — HỘP XÁC NHẬN CHO NÚT NGUỒN (dev_device đặt hàng).
   *
   * 🔴 Vì sao một cái nút gạt lại cần một hộp xác nhận, trong khi nó "gạt lại được": vì hậu quả
   *   thật của nó **không nằm trên hàng đang bấm**. Khoá một tài khoản là khoá **cả nhánh dưới
   *   nó**, ngay lập tức (`biChan` → `KHACH_HANG_HET_HAN`) — người bấm thấy một hàng đổi màu và
   *   không thấy ba người vừa bị đá ra khỏi hệ thống giữa ca trực.
   * ⇒ Hộp phải nói ra ĐÚNG HAI thứ người bấm không tự thấy: **tên tài khoản** và **số tài khoản
   *   con sẽ chết theo** — và con số ấy lấy từ chính cái cây đang vẽ (`soHauDue`), không đếm lại
   *   bằng một phép thứ hai sẽ trôi khỏi nó.
   */
  const [doiNguon, setDoiNguon] = useState<{ u: UserRow; soCon: number } | null>(null);

  /*
   * ═════════════════════════════════════════════════════════════════════════════════════
   * ➕ `GD-05` 11/09/2026 — Ô TÌM · LỌC TRẠNG THÁI · TÀI KHOẢN ĐANG CHỌN (mẫu `_BUS/25`).
   *
   * ⛔ BA THỨ NÀY CHỈ THU HẸP CÁI NHÌN, KHÔNG ĐỤNG VÀO DỮ LIỆU — cùng luật với ô tìm tuyến ở
   *   `v4` `C`: một tài khoản trôi khỏi bộ lọc vẫn còn nguyên trong cây, trong phép đếm, và
   *   trong mọi lệnh gửi đi. Ở đây điều đó rơi ra theo cấu tạo: `cay` dựng từ `users.data`,
   *   bộ lọc chỉ tác động lúc VẼ.
   * 🔴 VÀ BỐN Ô SỐ ĐẾM TRÊN **TOÀN BỘ**, không trên phần đang lọc. Gõ "khach" rồi thấy "Tổng
   *   tài khoản 1" thì người ta tưởng vừa mất hai tài khoản — đúng cái lee đã chỉ ra ở ô tìm
   *   tuyến ("để không tưởng mất tuyến"), và nó đúng y như thế ở đây.
   * ═════════════════════════════════════════════════════════════════════════════════════
   */
  const [tim, setTim] = useState('');
  const [locTrangThai, setLocTrangThai] = useState<'tat-ca' | 'dang-dung' | 'het-han' | 'bi-khoa'>('tat-ca');
  /*
   * ⚠ CHỌN theo **id**, không giữ cả object `UserRow`: giữ object là giữ một ẢNH CHỤP, và sau
   *   một lần Lưu thì bảng bên phải vẫn vẽ số cũ trong khi danh sách bên trái đã mới. Giữ id
   *   rồi tra lại từ `users.data` mỗi lần vẽ ⇒ một nguồn.
   * ⚠ `null` = chưa chọn gì ⇒ mặc định xem CHÍNH MÌNH (phiếu chốt). Không phải "không chọn ai":
   *   một bảng chi tiết trống giữa màn hình là một khối trông như đang tải dở.
   */
  const [dangXem, setDangXem] = useState<string | null>(null);
  /*
   * 🔑 "Đặt lại mật khẩu" mở ĐÚNG hộp Sửa (`GD-02b`), không dựng một hộp thứ hai. Một hộp thứ
   *   hai là một bản sao của bốn luật *(tự sửa · hạng · thời hạn · tuyến)*, và bản sao ấy sẽ
   *   trôi khỏi bản gốc đúng vào ngày một luật đổi.
   */
  const [mo0MatKhau, setMo0MatKhau] = useState(false);

  useEffect(() => { document.title = 'Quản trị — SDKD'; }, []);

  const isSuper = me?.role === 'SUPER_ADMIN';
  const bayGio = Date.now();

  /** `createdById` → tên, tra ngay trong danh sách đang có (cây của tôi + chính tôi). */
  const tenTheoId = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users.data ?? []) m.set(u.id, u.username);
    if (me) m.set(me.id, me.username);
    return m;
  }, [users.data, me]);

  /*
   * ── CÂY TÀI KHOẢN (lee đặt hàng lượt 58) ───────────────────────────────────────────────
   *
   * 🔴 Vì sao một bảng phẳng là chưa đủ, dù nó đã có cột "Người tạo": cột ấy trả lời được câu
   *   *"ai tạo hàng NÀY"* nhưng không trả lời được câu lee thật sự hỏi — *"khoá cái này thì
   *   chết bao nhiêu tài khoản"*. Vế sau là một câu về HÌNH DẠNG, và hình dạng thì phải nhìn
   *   thấy chứ không tra từng dòng.
   *
   * ⛔ Luật xếp nằm ở `lib/caytaikhoan.ts` (thuần, có phép `[96]` GỌI thẳng). Ở đây chỉ VẼ.
   */
  const cay = useMemo(() => xepCay(users.data ?? [], me?.id), [users.data, me?.id]);

  /*
   * Trạng thái THU nhớ qua lần mở sau (`localStorage`), và **khoá theo id người đăng nhập**:
   * hai tài khoản dùng chung một trình duyệt thì cây của họ khác nhau, id của họ cũng khác —
   * chung một khoá là người này thu nhánh của người kia.
   *
   * ⚠ MẶC ĐỊNH LÀ MỞ (lee chốt). Nên chỗ lưu chứa danh sách nút ĐANG THU — tập rỗng nghĩa là
   *   mở hết. Nếu lưu ngược lại (danh sách đang MỞ) thì một tài khoản mới sinh ra sau lần lưu
   *   sẽ mặc định THU, tức là mặc định tự đổi theo thời gian.
   */
  const khoaNho = me ? `sdkd.cay-taikhoan.thu.${me.id}` : null;
  const [dangThu, setDangThu] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!khoaNho) return;
    try {
      const t = JSON.parse(localStorage.getItem(khoaNho) ?? '[]');
      setDangThu(new Set(Array.isArray(t) ? t.filter((x): x is string => typeof x === 'string') : []));
    } catch { setDangThu(new Set()); }
  }, [khoaNho]);

  const doiThu = (id: string) => {
    setDangThu((cu) => {
      const moi = new Set(cu);
      if (moi.has(id)) moi.delete(id); else moi.add(id);
      if (khoaNho) { try { localStorage.setItem(khoaNho, JSON.stringify([...moi])); } catch { /* riêng tư/đầy — không phải lỗi đáng chặn */ } }
      return moi;
    });
  };

  const dongHien = useMemo(() => locThu(cay, dangThu), [cay, dangThu]);

  /*
   * ═════════════════════════════════════════════════════════════════════════════════════
   * ➕ `GD-05` — BỐN Ô SỐ, đếm trên **TOÀN BỘ cây của tôi**, không trên phần đang lọc.
   *
   * ⛔ `docHan()` là nguồn DUY NHẤT trả lời "còn hạn hay hết hạn" — cùng hàm mà mỗi dòng của
   *   danh sách đang gọi. Tự viết `new Date(u.expiresAt) < now` ở đây là dựng bản luật thứ
   *   hai, và bản ấy sẽ không biết `null = VĨNH VIỄN` (Luật số 0) vào ngày ai đó sửa nó.
   * ⚠ "Đang hoạt động" = `isActive` VÀ còn hạn. Một tài khoản còn bật mà đã hết hạn thì máy
   *   chủ chặn ở `napTaiKhoan` — đếm nó vào "đang hoạt động" là nói một điều màn hình đăng
   *   nhập sẽ cãi lại.
   */
  const soLieu = useMemo(() => {
    const ds = users.data ?? [];
    let hetHan = 0; let biKhoa = 0; let khach = 0; let vanHanh = 0;
    let hanGanNhat: string | null = null;
    for (const u of ds) {
      const h = docHan(u.expiresAt, bayGio);
      if (h.muc === 'het') hetHan += 1;
      if (!u.isActive) biKhoa += 1;
      if (u.role === 'ADMIN') {
        khach += 1;
        /* Hạn gần nhất TRONG CÂY — `null` (vĩnh viễn) không tham gia so sánh. */
        if (u.expiresAt && (!hanGanNhat || u.expiresAt < hanGanNhat)) hanGanNhat = u.expiresAt;
      }
      if (u.role === 'MANAGER') vanHanh += 1;
    }
    const dangDung = ds.filter((u) => u.isActive && docHan(u.expiresAt, bayGio).muc !== 'het').length;
    return { tong: ds.length, dangDung, hetHan, biKhoa, khach, vanHanh, hanGanNhat };
  }, [users.data, bayGio]);

  /*
   * ⛔ LỌC CHỈ THU HẸP CÁI NHÌN. `khoaTim()` dùng chung với ô tìm tuyến (`lib/khongdau.ts`) —
   *   gõ không dấu vẫn ra, và **một bộ tìm thứ hai là một bộ sẽ xử sự khác** ở đúng những
   *   chữ tiếng Việt khó.
   * ⚠ Lọc chạy trên `dongHien` (đã bỏ nhánh đang thu), nên nó KHÔNG tự mở nhánh để khoe kết
   *   quả. Đó là một quyết định: người dùng thu một nhánh là họ nói "đừng cho tôi thấy cái
   *   này", và một ô tìm không được quyền cãi lại. Câu dưới danh sách nói ra số bị giấu.
   */
  const dongLoc = useMemo(() => {
    const kw = khoaTim(tim);
    return dongHien.filter((n) => {
      const u = n.hang;
      if (locTrangThai === 'dang-dung' && !(u.isActive && docHan(u.expiresAt, bayGio).muc !== 'het')) return false;
      if (locTrangThai === 'het-han' && docHan(u.expiresAt, bayGio).muc !== 'het') return false;
      if (locTrangThai === 'bi-khoa' && u.isActive) return false;
      if (!kw) return true;
      return khoaTim(`${u.username} ${u.fullName} ${u.email ?? ''}`).includes(kw);
    });
  }, [dongHien, tim, locTrangThai, bayGio]);

  /** Tài khoản đang xem ở bảng phải — mặc định CHÍNH MÌNH (phiếu chốt). */
  const uXem = useMemo(() => {
    const ds = users.data ?? [];
    return ds.find((u) => u.id === dangXem) ?? ds.find((u) => u.id === me?.id) ?? ds[0] ?? null;
  }, [users.data, dangXem, me?.id]);

  /*
   * ═════════════════════════════════════════════════════════════════════════════════════
   * `GD-01` — LỒNG LẠI CHO `<Tree>` CỦA FLUENT, và nói rõ **vì sao đây KHÔNG phải một bản
   * luật thứ hai** (L67 cấm hai bản của một luật).
   *
   * `xepCay()` trả một danh sách **PHẲNG, đã duyệt sâu**, mỗi nút mang `mucSau`. `locThu()` bỏ
   * đi phần nằm dưới nút đang thu. Cả hai giữ nguyên — chúng là LUẬT: ai là con ai, thứ tự
   * thế nào, hàng nào mồ côi.
   * Fluent `<Tree>` thì cần cấu trúc **LỒNG**. Hàm dưới đây chỉ làm một việc cơ học: đọc
   * `mucSau` của một danh sách ĐÃ ĐÚNG THỨ TỰ rồi gắn con vào cha gần nhất. Nó không quyết định
   * ai là con ai — nó chỉ đọc lại quyết định đã có.
   *
   * ⚠ VÀ NÓ DỰNG TỪ `dongLoc` — tức từ `dongHien` ĐÃ LỌC, KHÔNG từ `cay`: như thế **một nguồn
   *   duy nhất** quyết định cái gì hiện ra — `locThu` (thu/sổ) rồi tới bộ lọc, cả hai đều chỉ
   *   THU HẸP. Dựng từ `cay` rồi để `openItems` của Fluent tự giấu là dựng **hai** nguồn cho
   *   một câu hỏi, và ngày chúng lệch nhau thì màn hình nói một đằng, `localStorage` nhớ một nẻo.
   * ⛔ `GD-05` — và bộ lọc phải đứng SAU `locThu`, không thay nó: lọc trên `users.data` thì gõ
   *   một chữ vào ô tìm là nhánh đang thu bật dậy, tức ô tìm cãi lại chính người vừa thu nó.
   * ═════════════════════════════════════════════════════════════════════════════════════
   */
  const goc = useMemo(() => longTheoMuc(dongLoc), [dongLoc]);

  /**
   * Nút nào ĐANG MỞ — Fluent hỏi theo chiều dương, ta nhớ theo chiều âm (`dangThu`).
   * ⚠ Chỉ kể nút CÓ CON: một lá nằm trong `openItems` là một giá trị vô nghĩa với `<Tree>`.
   */
  const dangMo = useMemo(
    () => cay.filter((n) => n.soConTrucTiep > 0 && !dangThu.has(n.hang.id)).map((n) => n.hang.id),
    [cay, dangThu],
  );

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/users/${id}`),
    onSuccess: () => {
      toast.success('Đã xoá tài khoản');
      setDeleting(null);
      qc.invalidateQueries({ queryKey: qk.users });
    },
    /*
     * 🔴 409 KHÔNG PHẢI MỘT LỖI CHUNG CHUNG — nó là một câu trả lời có DỮ LIỆU: *"tài khoản này
     *   đã tạo N tài khoản khác, đây là tên chúng"*. Nuốt nó vào một dòng `toast` đỏ là vứt đi
     *   đúng phần người bấm cần để đi tiếp.
     */
    onError: (e) => {
      const b = e instanceof ApiError && e.status === 409 ? e.body : null;
      if (b && Array.isArray(b.conCuaTaiKhoan)) {
        setConChanXoa({ ten: deleting?.username ?? '', con: b.conCuaTaiKhoan });
        setDeleting(null);
        return;
      }
      toast.error('Không xoá được tài khoản', errorMessage(e));
    },
  });

  const doiTrangThai = useMutation({
    mutationFn: ({ u }: { u: UserRow; soCon: number }) =>
      api.patch(`/api/users/${u.id}`, { isActive: !u.isActive }),
    onSuccess: (_r, { u, soCon }) => {
      setDoiNguon(null);
      /*
       * ⚠ NÓI RA VẾ LAN XUỐNG. Khoá một tài khoản là khoá **cả nhánh dưới nó** (máy chủ:
       *   `biChan` → `KHACH_HANG_HET_HAN`). Người bấm chỉ thấy một hàng đổi màu, nên nếu không
       *   nói thì hậu quả thật nằm ngoài tầm mắt họ.
       */
      toast.success(u.isActive ? `Đã vô hiệu hoá @${u.username}` : `Đã kích hoạt lại @${u.username}`,
        soCon > 0
          ? (u.isActive
              ? `${soCon} tài khoản trong nhánh dưới cũng dừng theo, ngay lập tức.`
              : `${soCon} tài khoản trong nhánh dưới cũng vào lại được, trừ những cái bị khoá riêng.`)
          : undefined);
      qc.invalidateQueries({ queryKey: qk.users });
    },
    onError: (e) => toast.error('Không đổi được trạng thái', errorMessage(e)),
  });

  /*
   * ═════════════════════════════════════════════════════════════════════════════════════
   * TK-45 §6 (lee 25/10, mẫu `y04c` danh sách · `y04d` ngăn kéo) — BỐ CỤC MỚI, LOGIC CŨ.
   *   · Đầu trang: đường dẫn · chip "Mô phỏng" · "Quản trị" + dòng phụ (chữ của mẫu lee duyệt) · "+ Thêm tài khoản".
   *   · Thẻ "Tài khoản (N)": tìm · Vai trò · Tình trạng → BẢNG 4 cột, thụt lề theo CÂY cha → con (↳) — vẫn `xepCay` → `locThu`
   *     → lọc (một nguồn, chỉ THU HẸP cái nhìn); nhánh có con vẫn thu / mở được (nhớ theo `localStorage` như cũ).
   *   · Bấm một dòng ⇒ NGĂN KÉO Fluent (`OverlayDrawer`: nền mờ, Esc / bấm ra ngoài thì đóng) "Thông tin tài khoản": Chỉnh sửa ·
   *     Khoá (hộp xác nhận cũ, nói số tài khoản con dừng theo) · Đặt lại mật khẩu · Xoá + bảng "Chi tiết & phân quyền" cũ.
   *   · Hai thẻ gập: "Vai trò và quyền truy cập" (4 ô số + các hạng) · "Chẩn đoán hệ thống" (sức khoẻ máy chủ · firmware).
   *   ⛔ Không bỏ chức năng nào; hộp Tạo/Sửa (`TaiKhoanEditor`) giữ nguyên. Trang vẽ bằng Fluent ⇒ màu chỉ qua `useStyles` (token).
   * ═════════════════════════════════════════════════════════════════════════════════════
   */
  const [locVai, setLocVai] = useState<Role | 'tat-ca'>('tat-ca');
  const [moNgan, setMoNgan] = useState(false);
  const [moVaiTro, setMoVaiTro] = useState(false);
  const [moChanDoan, setMoChanDoan] = useState(false);
  const moPhong = useMoPhong().data;
  /* Vai trò là bộ lọc THỨ BA, đứng SAU `dongLoc` (tìm + tình trạng) — cùng luật: chỉ thu hẹp cái nhìn */
  const dongBang = useMemo(() => (locVai === 'tat-ca' ? dongLoc : dongLoc.filter((n) => n.hang.role === locVai)), [dongLoc, locVai]);
  const vaiCo = useMemo(() => THU_TU_VAI.filter((v) => (users.data ?? []).some((u) => u.role === v)), [users.data]);
  const datLaiLoc = () => { setTim(''); setLocTrangThai('tat-ca'); setLocVai('tat-ca'); };
  const chonHang = (u: UserRow) => { setDangXem(u.id); setMoNgan(true); };
  const nutUXem = cay.find((n) => n.hang.id === uXem?.id) ?? null;
  const LOP_VAI: Record<Role, string> = { SUPER_ADMIN: s.vaiSuper, ADMIN: s.timNen, MANAGER: s.vaiManager, OPERATOR: s.vaiOperator, VIEWER: s.vaiViewer };
  const chipVai = (r: Role) => <span className={`inline-block whitespace-nowrap rounded px-2 py-0.5 text-[12px] ${LOP_VAI[r]}`} data-vai={r}>{tenVai(r)}</span>;
  const MAU_TT = { tot: [s.totCham, s.totChu], canh: [s.canhCham, s.canhChu], xam: [s.xamCham, s.mo] } as const;
  const veTinhTrang = (u: UserRow) => {
    const tt = tinhTrangTK(u, bayGio);
    return <span className="flex items-center gap-1.5 text-[12.5px]"><span className={`h-1.5 w-1.5 shrink-0 rounded-full ${MAU_TT[tt.mau][0]}`} /><span className={MAU_TT[tt.mau][1]}>{tt.nhan}</span></span>;
  };
  /* màu ảnh đại diện: MỘT biểu thức cho bảng + danh sách thẻ (chỗ thứ hai là bảng chi tiết) */
  const lopAnh = (u: UserRow) => `${s.anhDaiDien} ${u.role === 'SUPER_ADMIN' ? s.avSuper : u.role === 'ADMIN' ? s.avAdmin : s.avManager}`;

  const veHang = (n: NutCay<UserRow>) => {
    const u = n.hang;
    const coCon = n.soConTrucTiep > 0;
    const thu = dangThu.has(u.id);
    return (
      <tr key={u.id} onClick={() => chonHang(u)} data-tai-khoan={u.username} className={`${s.hangBang} ${uXem?.id === u.id ? s.hangDangChon : ''}`}>
        <td className="py-3 pl-5 pr-3">
          <span className="flex min-w-0 flex-wrap items-center gap-2" style={{ paddingLeft: n.mucSau * 22 }}>
            {n.mucSau > 0 && <span className={`shrink-0 ${s.mo}`} aria-hidden="true">↳</span>}
            <span aria-hidden="true" className={lopAnh(u)}>{viTat(u.username)}</span>
            <b className={`min-w-0 truncate text-[14px] ${s.chu1}`}>{u.username}</b>
            {u.id === me?.id && <Badge color="brand" appearance="tint">bạn</Badge>}
            {coCon && (
              <button type="button" onClick={(e) => { e.stopPropagation(); doiThu(u.id); }} aria-expanded={!thu} data-o="thu-nhanh"
                aria-label={thu ? `Mở nhánh @${u.username}` : `Thu nhánh @${u.username}`} className={`shrink-0 rounded px-1 text-xs ${s.nutChu}`}>
                {thu ? '▸' : '▾'}
              </button>
            )}
            {coCon && (
              <Badge appearance="filled" className={s.hhTaiKhoanPhu}>
                {n.soConTrucTiep} tài khoản phụ
                {thu && n.soHauDue > n.soConTrucTiep ? ` · ẩn ${n.soHauDue}` : ''}
              </Badge>
            )}
            {n.moCoi && <Badge color="warning" appearance="tint">không có người tạo</Badge>}
          </span>
        </td>
        <td className="px-3 py-3">{chipVai(u.role)}</td>
        <td className="px-3 py-3">{veTinhTrang(u)}</td>
        <td className={`px-3 py-3 text-[13px] ${s.chu1}`}>{phamViTK(u)}</td>
        <td className={`pr-5 text-right text-lg ${s.nhanChu}`} aria-hidden="true">›</td>
      </tr>
    );
  };

  return (
    <div className="space-y-5" data-o="trang-quan-tri">
      {/* ══════════ ĐẦU TRANG ══════════ */}
      <div className={`-mt-1 flex items-center gap-2 pb-3 text-xs ${s.vienDuoi}`} data-o="duong-dan">
        <span className={s.nhanChu}>Không gian làm việc</span><span className={s.mo}>/</span><span className={`font-semibold ${s.chu1}`}>Quản trị</span>
        {moPhong?.bat === true && <span className={`ml-auto flex items-center gap-1.5 ${s.canhChu}`} data-o="chip-mo-phong"><span className={`h-1.5 w-1.5 rounded-full ${s.canhCham}`} />Mô phỏng</span>}
      </div>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0">
          <h1 className={`chu-tieude m-0 text-[30px] font-semibold leading-tight ${s.tieuDeTrang}`}>Quản trị</h1>
          <p className={`m-0 mt-1.5 text-sm ${s.chu2}`}>Quản lý tài khoản và phạm vi truy cập hệ thống.</p>
        </div>
        {/* ⚠ `ml-auto` đặt trên VỎ, không trên `<Button>`: lớp Griffel của Fluent đặt `margin: 0` và thắng lớp Tailwind (đo: 0px) */}
        <span className="ml-auto">
          <Button appearance="primary" icon={<PersonAdd20Regular />} onClick={() => setEditing('new')} data-o="them-tai-khoan">
            Thêm tài khoản
          </Button>
        </span>
      </div>

      {/* ══════════ THẺ TÀI KHOẢN ══════════ */}
      <section className={s.the} data-o="the-tai-khoan">
        <div className="flex flex-wrap items-start gap-3 px-5 pb-4 pt-5">
          <div className="min-w-0">
            <h2 className={`m-0 flex items-center gap-2 text-lg font-semibold ${s.chu1}`}>Tài khoản
              <span className={`px-1.5 text-xs font-semibold ${s.soDem}`} data-o="so-tai-khoan">{users.data ? users.data.length : '—'}</span></h2>
            <p className={`m-0 mt-1 text-[13px] ${s.chu2}`}>Chọn tài khoản để xem thông tin và chỉnh sửa.</p>
          </div>
          <span className={`ml-auto max-w-full truncate px-2.5 py-1 text-xs ${s.chipVien}`} data-o="chip-cong-ty">Công ty SDKD</span>
        </div>
        {users.data && users.data.length > 0 && (
          <div className={`flex flex-wrap items-center gap-3 px-5 py-4 ${s.vienTren}`} data-o="loc-tai-khoan">
            <SearchBox value={tim} onChange={(_e, d) => setTim(d.value)} placeholder="Tìm tên tài khoản…" className="min-w-[200px] flex-1 sm:max-w-[460px]" />
            <label className={`flex items-center gap-2 text-xs ${s.chu2}`}>Vai trò
              <select value={locVai} onChange={(e) => setLocVai(e.target.value as Role | 'tat-ca')} data-o="loc-vai" className={s.oChon}>
                <option value="tat-ca">Tất cả vai trò</option>
                {vaiCo.map((v) => <option key={v} value={v}>{tenVai(v)}</option>)}
              </select>
            </label>
            <label className={`flex items-center gap-2 text-xs ${s.chu2}`}>Tình trạng
              <select value={locTrangThai} onChange={(e) => setLocTrangThai(e.target.value as typeof locTrangThai)} data-o="loc-tinh-trang" className={s.oChon}>
                <option value="tat-ca">Tất cả tình trạng</option>
                <option value="dang-dung">Đang hoạt động</option>
                <option value="het-han">Hết hạn</option>
                <option value="bi-khoa">Đã khoá</option>
              </select>
            </label>
          </div>
        )}
        {users.isLoading && <div className="px-5 pb-5"><Spinner label="Đang tải danh sách tài khoản…" /></div>}
        {users.isError && <div className="px-5 pb-5"><ErrorBox error={users.error} onRetry={() => void users.refetch()} /></div>}
        {users.data && users.data.length === 0 && <EmptyState icon={<PersonAdd20Regular />} title="Chưa có tài khoản nào" />}
        {users.data && users.data.length > 0 && (
          <>
            {/* ≥ 640 px: bảng 4 cột */}
            <table className="hidden w-full table-fixed text-left sm:table" data-o="bang-tai-khoan">
              <thead className={`text-xs ${s.dauBang}`}>
                <tr><th className="w-[46%] py-3 pl-5 pr-3 font-semibold">Tài khoản</th><th className="px-3 py-3 font-semibold">Vai trò</th>
                  <th className="px-3 py-3 font-semibold">Tình trạng</th><th className="px-3 py-3 font-semibold">Phạm vi tuyến</th><th className="w-10" /></tr>
              </thead>
              <tbody>{dongBang.map(veHang)}</tbody>
            </table>
            {/* 390 px: danh sách thẻ */}
            <ul className="m-0 list-none p-0 sm:hidden" data-o="the-tai-khoan-dt">
              {dongBang.map((n) => {
                const u = n.hang;
                return (
                  <li key={u.id} className={s.vienTren}>
                    <button type="button" onClick={() => chonHang(u)} className="flex w-full items-center gap-3 border-0 bg-transparent py-3 pr-4 text-left" style={{ paddingLeft: 16 + n.mucSau * 16 }}>
                      <span aria-hidden="true" className={lopAnh(u)}>{viTat(u.username)}</span>
                      <span className="flex min-w-0 flex-1 flex-col gap-1">
                        <b className={`truncate text-sm ${s.chu1}`}>{n.mucSau > 0 ? '↳ ' : ''}{u.username}</b>
                        <span className={`flex flex-wrap items-center gap-2 text-xs ${s.chu2}`}>{chipVai(u.role)}{veTinhTrang(u)}<span>{phamViTK(u)}</span></span>
                      </span>
                      <span className={`text-lg ${s.nhanChu}`} aria-hidden="true">›</span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {/*
              * ⚠ RỖNG VÌ BỘ LỌC ≠ RỖNG VÌ CHƯA CÓ TÀI KHOẢN — hai ca, hai câu. Lọc vai trò rỗng (tìm + tình trạng còn hàng) cũng nói ra.
              */}
            {users.data && users.data.length > 0 && dongLoc.length === 0 && (
              <EmptyState icon={<PersonAdd20Regular />} title="Không tài khoản nào khớp" hint={`Bộ lọc chỉ giấu bớt — ${dongHien.length} hàng vẫn còn nguyên trong cây.`} />
            )}
            {dongLoc.length > 0 && dongBang.length === 0 && (
              <EmptyState icon={<PersonAdd20Regular />} title="Không tài khoản nào khớp" hint={`Bộ lọc chỉ giấu bớt — ${dongHien.length} hàng vẫn còn nguyên trong cây.`} />
            )}
            <div className={`flex items-center gap-3 px-5 py-3 text-xs ${s.chu2} ${s.vienTren}`} data-o="chan-the">
              <span><b className={s.chu1}>{dongBang.length}</b> / {users.data.length} tài khoản</span>
              <button type="button" onClick={datLaiLoc} className={`ml-auto text-xs hover:underline ${s.nutChu}`} data-o="dat-lai-loc">Đặt lại bộ lọc</button>
            </div>
          </>
        )}
      </section>

      {/* ══════════ HAI THẺ GẬP ══════════ */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 [&>*]:min-w-0">
        <section className={s.the} data-o="the-vai-tro">
          <button type="button" onClick={() => setMoVaiTro((v) => !v)} aria-expanded={moVaiTro} className="flex w-full items-center gap-3 border-0 bg-transparent px-5 py-4 text-left">
            <span className={`grid h-8 w-8 shrink-0 place-items-center ${s.chipVien}`} aria-hidden="true">?</span>
            <span className="min-w-0 flex-1"><b className={`block text-[15px] font-semibold ${s.chu1}`}>Vai trò và quyền truy cập</b>
              <span className={`text-xs ${s.nhanChu}`}>Phân biệt Quản trị hệ thống, Khách hàng và Vận hành</span></span>
            <span className={s.mo} aria-hidden="true">{moVaiTro ? '▴' : '▾'}</span>
          </button>
          {moVaiTro && (
            <div className={`space-y-4 p-5 ${s.vienTren}`}>
              {/* ⛔ ĐẾM TRÊN TOÀN BỘ cây của mình, không trên phần đang lọc (GD-05) */}
              <div data-vung="so-tai-khoan" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <OSoTK icon={<People20Regular />} mau="nhan" nhan="Tổng tài khoản" tri={soLieu.tong} />
                <OSoTK icon={<CheckmarkCircle20Regular />} mau="tot" nhan="Đang hoạt động" tri={soLieu.dangDung}
                  phu={soLieu.hetHan + soLieu.biKhoa > 0 ? `${soLieu.hetHan} hết hạn · ${soLieu.biKhoa} bị khoá` : 'không tài khoản nào hết hạn hay bị khoá'} />
                <OSoTK icon={<Person20Regular />} mau="tim" nhan="Khách hàng" tri={soLieu.khach}
                  phu={soLieu.hanGanNhat ? `hạn gần nhất ${fmtDate(soLieu.hanGanNhat)}` : soLieu.khach > 0 ? 'tất cả vĩnh viễn' : undefined} />
                <OSoTK icon={<Settings20Regular />} mau="canh" nhan="Vận hành" tri={soLieu.vanHanh} />
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="flex flex-wrap items-center gap-2">{chipVai('SUPER_ADMIN')}<Caption1 className={s.mo}>Toàn hệ thống — tạo được mọi hạng tài khoản.</Caption1></span>
                {HANG_HIEN.map((h) => (
                  <span key={h.vai} className="flex flex-wrap items-center gap-2">{chipVai(h.vai)}<Caption1 className={s.mo}>{h.mo}</Caption1></span>
                ))}
              </div>
              <Caption1 className={s.mo}>
                Làm được gì thì do <b>tính năng</b> (5 ô tick) và <b>tuyến</b> quyết định, không do hạng.
                Hạng chỉ quyết định tài khoản ấy có tạo được tài khoản phụ hay không.
              </Caption1>
            </div>
          )}
        </section>
        <section className={s.the} data-o="the-chan-doan">
          <button type="button" onClick={() => setMoChanDoan((v) => !v)} aria-expanded={moChanDoan} className="flex w-full items-center gap-3 border-0 bg-transparent px-5 py-4 text-left">
            <span className={`grid h-8 w-8 shrink-0 place-items-center ${s.chipVien}`} aria-hidden="true">⌘</span>
            <span className="min-w-0 flex-1"><b className={`block text-[15px] font-semibold ${s.chu1}`}>Chẩn đoán hệ thống</b>
              <span className={`text-xs ${s.nhanChu}`}>Máy chủ và firmware</span></span>
            <span className={s.mo} aria-hidden="true">{moChanDoan ? '▴' : '▾'}</span>
          </button>
          {moChanDoan && (
            <div className={`space-y-4 p-5 ${s.vienTren}`}>
              <TheSucKhoeMayChu />
              {me?.role === 'SUPER_ADMIN' && <TheBanFirmware />}
            </div>
          )}
        </section>
      </div>

      {/* ══════════ NGĂN KÉO "THÔNG TIN TÀI KHOẢN" — Fluent OverlayDrawer (nền mờ · Esc · bấm ra ngoài) ══════════ */}
      <OverlayDrawer position="end" open={moNgan && !!uXem} onOpenChange={(_e, d) => setMoNgan(d.open)} className={s.ngan} data-o="ngan-keo-tai-khoan">
        {uXem && (
          <div className="flex h-full flex-col gap-4 overflow-y-auto px-6 pb-8 pt-6">
            <div className="flex items-start gap-2">
              <span className={s.nhanNgan}>Thông tin tài khoản</span>
              <span className="ml-auto"><Button appearance="subtle" icon={<Dismiss20Regular />} aria-label="Đóng" onClick={() => setMoNgan(false)} data-o="dong-ngan" /></span>
            </div>
            <h2 className={`m-0 break-all text-[26px] font-semibold leading-tight ${s.chu1}`}>{uXem.username}</h2>
            {veTinhTrang(uXem)}
            <dl className="m-0 grid grid-cols-[130px_minmax(0,1fr)] gap-x-3 gap-y-4 text-[13.5px]" data-o="dong-thong-tin">
              <dt className={s.chu2}>Vai trò</dt><dd className={`m-0 ${s.chu1}`}>{tenVai(uXem.role)}</dd>
              <dt className={s.chu2}>Loại tài khoản</dt><dd className={`m-0 ${s.chu1}`}>{loaiTK(uXem)}</dd>
              <dt className={s.chu2}>Phạm vi tuyến</dt><dd className={`m-0 ${s.chu1}`}>{phamViTK(uXem)}</dd>
              <dt className={s.chu2}>Quyền truy cập</dt><dd className={`m-0 ${s.chu1}`}>{uXem.role === 'SUPER_ADMIN' ? 'Toàn hệ thống' : `${uXem.quyen.length} tính năng`}</dd>
              <dt className={s.chu2}>Người tạo</dt><dd className={`m-0 ${s.chu1}`}>{veNguoiTao(uXem, tenTheoId, nutUXem?.moCoi ?? false, s.canhBao)}</dd>
            </dl>
            <div className={`flex flex-wrap gap-2 pt-4 ${s.vienTren}`}>
              <Button appearance="primary" icon={<Edit20Regular />} onClick={() => setEditing(uXem)} data-o="ngan-chinh-sua">Chỉnh sửa</Button>
              {/* ⛔ KHÔNG bắn thẳng: mở hộp xác nhận cũ (nói số tài khoản con dừng theo); tài khoản đang đăng nhập không tự khoá */}
              <Button icon={<Power20Regular />} disabled={uXem.id === me?.id} title={uXem.id === me?.id ? 'Không tự khoá tài khoản đang đăng nhập' : undefined}
                onClick={() => setDoiNguon({ u: uXem, soCon: nutUXem?.soHauDue ?? 0 })} data-o="ngan-khoa">
                {uXem.isActive ? 'Khoá tài khoản' : 'Mở khoá tài khoản'}
              </Button>
              <Button appearance="subtle" icon={<Key20Regular />} onClick={() => { setEditing(uXem); setMo0MatKhau(true); }}>Đặt lại mật khẩu</Button>
              {uXem.id !== me?.id && <Button appearance="subtle" icon={<Delete20Regular />} onClick={() => setDeleting(uXem)}>Xoá</Button>}
            </div>
            <ChiTietPhanQuyen
              u={uXem}
              me={me}
              tenTheoId={tenTheoId}
              cha={(users.data ?? []).find((x) => x.id === uXem?.createdById) ?? null}
              bayGio={bayGio}
              soCon={nutUXem?.soConTrucTiep ?? 0}
              onSua={() => uXem && setEditing(uXem)}
              onDoiMatKhau={() => { if (uXem) { setEditing(uXem); setMo0MatKhau(true); } }}
            />
          </div>
        )}
      </OverlayDrawer>

      {editing && (
        <TaiKhoanEditor
          user={editing === 'new' ? null : editing}
          isSuper={isSuper}
          cuonToiMatKhau={mo0MatKhau}
          onClose={() => { setEditing(null); setMo0MatKhau(false); }}
        />
      )}

      {/* ── XOÁ TÀI KHOẢN ─────────────────────────────────────────────────────────────── */}
      <Dialog open={!!deleting} onOpenChange={(_e, d) => { if (!d.open && d.type !== 'backdropClick') setDeleting(null); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Xoá tài khoản</DialogTitle>
            <DialogContent>
              Xoá vĩnh viễn tài khoản <b>@{deleting?.username}</b> ({deleting?.fullName})? Nhật ký
              thao tác cũ vẫn được giữ lại, nhưng <b>không còn tên người làm</b>.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" disabled={remove.isPending} onClick={() => setDeleting(null)}>
                Huỷ
              </Button>
              <Button
                appearance="primary"
                className={s.nutNguy}
                disabled={remove.isPending}
                icon={remove.isPending ? <Spinner size="tiny" /> : <Delete20Regular />}
                onClick={() => deleting && remove.mutate(deleting.id)}
              >
                Xoá tài khoản
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/*
        * ── HỘP XÁC NHẬN NÚT NGUỒN ─────────────────────────────────────────────────────
        * ⚠ Hai chiều, hai câu, và câu KHOÁ nặng hơn hẳn — nên màu nút cũng đi theo chiều:
        *   khoá là một việc **đá người ra khỏi hệ thống**; mở lại thì không mất gì.
        */}
      <Dialog open={!!doiNguon} onOpenChange={(_e, d) => { if (!d.open && d.type !== 'backdropClick') setDoiNguon(null); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {doiNguon?.u.isActive ? 'Vô hiệu hoá tài khoản' : 'Kích hoạt lại tài khoản'}
            </DialogTitle>
            <DialogContent>
              {doiNguon?.u.isActive ? (
                <>
                  Khoá <b>@{doiNguon.u.username}</b> ({doiNguon.u.fullName})? Tài khoản này sẽ{' '}
                  <b>không đăng nhập được nữa</b>, và phiên đang mở của họ bị chặn ngay ở yêu cầu
                  kế tiếp.
                  {doiNguon.soCon > 0 && (
                    <>
                      {' '}
                      {/* 🔴 CON SỐ LÀ PHẦN ĐÁNG GIÁ NHẤT CỦA HỘP NÀY — nó là thứ người bấm không tự thấy. */}
                      <b className={s.hetHan}>{doiNguon.soCon} tài khoản trong nhánh dưới</b> cũng
                      dừng theo, ngay lập tức — kể cả khi từng cái vẫn đang bật.
                    </>
                  )}
                </>
              ) : (
                <>
                  Mở lại <b>@{doiNguon?.u.username}</b> ({doiNguon?.u.fullName})? Họ đăng nhập lại
                  được ngay.
                  {(doiNguon?.soCon ?? 0) > 0 && (
                    <>
                      {' '}
                      <b>{doiNguon?.soCon} tài khoản trong nhánh dưới</b> cũng vào lại được — trừ
                      những cái đang bị khoá riêng, chúng vẫn khoá.
                    </>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" disabled={doiTrangThai.isPending} onClick={() => setDoiNguon(null)}>
                Huỷ
              </Button>
              <Button
                appearance="primary"
                className={doiNguon?.u.isActive ? s.nutNguy : undefined}
                disabled={doiTrangThai.isPending}
                icon={doiTrangThai.isPending ? <Spinner size="tiny" /> : <Power20Regular />}
                onClick={() => doiNguon && doiTrangThai.mutate(doiNguon)}
              >
                {doiNguon?.u.isActive ? 'Vô hiệu hoá' : 'Kích hoạt lại'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 409 — không xoá được vì còn con. Bày TÊN ra, và chỉ đúng đường đi tiếp. */}
      <Dialog open={!!conChanXoa} onOpenChange={(_e, d) => { if (!d.open && d.type !== 'backdropClick') setConChanXoa(null); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Không xoá được — tài khoản này còn tài khoản con</DialogTitle>
            <DialogContent>
              <p>
                <b>@{conChanXoa?.ten}</b> đã tạo {conChanXoa?.con.length} tài khoản. Xoá nó sẽ để
                lại những tài khoản ấy <b>không ai quản</b> — nên máy chủ từ chối.
              </p>
              <ul className="mt-2 list-disc pl-5">
                {(conChanXoa?.con ?? []).map((c) => <li key={c}>@{c}</li>)}
              </ul>
              <p className="mt-3">
                Cách đi tiếp: xoá từng tài khoản con trước, hoặc <b>vô hiệu hoá</b> tài khoản này —
                khoá một tài khoản là khoá cả nhánh dưới nó, ngay lập tức.
              </p>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConChanXoa(null)}>Đã hiểu</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}

/* ───────── TK-45 §6: chữ của bảng tài khoản (cấp module; màu ở `useStyles`) ───────── */
const THU_TU_VAI: Role[] = ['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'OPERATOR', 'VIEWER'];
/** Nhãn vai trò (phiếu TK-45 §6): hạng có trong `HANG_HIEN` đọc `tenHang`; OPERATOR = Quản lý · VIEWER = Xem. */
const tenVai = (r: Role) => (r === 'OPERATOR' ? 'Quản lý' : r === 'VIEWER' ? 'Xem' : tenHang(r));
/** Tình trạng: đã khoá (xám) · hết hạn (hổ phách — cần người gia hạn) · đang hoạt động (lá). `docHan` là nguồn duy nhất của "hết hạn". */
function tinhTrangTK(u: UserRow, bayGio: number): { nhan: string; mau: 'tot' | 'canh' | 'xam' } {
  if (!u.isActive) return { nhan: 'Đã khoá', mau: 'xam' };
  if (docHan(u.expiresAt, bayGio).muc === 'het') return { nhan: 'Hết hạn', mau: 'canh' };
  return { nhan: 'Đang hoạt động', mau: 'tot' };
}
const phamViTK = (u: UserRow) => (u.role === 'SUPER_ADMIN' ? 'Toàn bộ tuyến' : u.routeAccess.length === 0 ? 'Chưa cấp tuyến' : `${u.routeAccess.length} tuyến`);
const loaiTK = (u: UserRow) => (u.role === 'SUPER_ADMIN' ? 'Gốc công ty' : u.role === 'ADMIN' ? 'Tài khoản khách' : 'Tài khoản con');
/** Ô chữ cái đầu: hai chữ đầu của tên tài khoản ("admin_cs" ⇒ "AD"). */
const viTat = (ten: string) => ten.replace(/[^\p{L}\p{N}]/gu, '').slice(0, 2).toUpperCase() || '?';
/**
 * "Người tạo" — BA ca (giữ từ dòng phụ cũ của cây): có tên trong tầm nhìn ⇒ tên · có id mà tra không ra ⇒ ở trên bạn (khách đăng
 * nhập) · không có ⇒ mồ côi (hổ phách) hoặc gốc.
 */
function veNguoiTao(u: UserRow, tenTheoId: Map<string, string>, moCoi: boolean, lopCanh: string) {
  return u.createdById
    ? (tenTheoId.has(u.createdById)
        ? <span>người tạo @{tenTheoId.get(u.createdById)}</span>
        : <span title="Người tạo nằm ngoài phạm vi bạn được xem">người tạo ở trên bạn</span>)
    : moCoi
      ? <span className={lopCanh}>không có người tạo</span>
      : <span>gốc</span>;
}

/* ------------------------------------------------------------ soạn tài khoản */

function TaiKhoanEditor({
  user, isSuper, onClose, cuonToiMatKhau = false,
}: {
  user: UserRow | null;
  isSuper: boolean;
  onClose: () => void;
  /**
   * ➕ `GD-05` — mở từ nút 🔑 "Đặt lại mật khẩu": cuộn tới ô mật khẩu và đặt con trỏ vào đó.
   *
   * ⛔ CHỈ CUỘN, KHÔNG ĐỔI GÌ KHÁC. Một cái "chế độ đặt lại mật khẩu" giấu bớt các ô khác sẽ
   *   là một hộp thứ hai đội lốt một cờ — và nó sẽ trôi khỏi hộp gốc đúng vào ngày một luật
   *   đổi. Đây là một LỐI TẮT tới cùng một chỗ, không phải một màn hình khác.
   */
  cuonToiMatKhau?: boolean;
}) {
  const s = useStyles();
  const isNew = !user;
  const { user: me } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const routes = useRoutes();

  /* ── ① thông tin ─────────────────────────────────────────────────────────────────── */
  const [username, setUsername] = useState(user?.username ?? '');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState(user?.fullName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');

  /*
   * ── ② hạng ──────────────────────────────────────────────────────────────────────────
   * ⛔ KHÁCH TẠO THÌ HẠNG CỐ ĐỊNH LÀ **Vận hành** (lee chốt). Không phải một ô chọn bị làm mờ
   *   cho đẹp: form không dựng ô chọn, và máy chủ vẫn chặn ĐỘC LẬP (`duocPhepDatVai` đòi vai
   *   THẤP HƠN HẲN). Hai chốt, vì một chốt ở trình duyệt không phải một chốt.
   */
  const [role, setRole] = useState<Role>(user?.role ?? (isSuper ? 'ADMIN' : 'MANAGER'));

  /* ── ③ thời hạn ─────────────────────────────────────────────────────────────────── */
  /*
   * ⚠ BỐN LOẠI GIÁ TRỊ, và chúng KHÔNG gộp được — mỗi cái là một câu khác nhau:
   *   `'giu'`      không nói gì về thời hạn (sửa tên, sửa điện thoại…)  ⇒ KHÔNG gửi trường
   *   `null`       VĨNH VIỄN — một mốc thật                              ⇒ gửi `null`
   *   một số tháng gói                                                   ⇒ gửi mốc tính từ BÂY GIỜ
   *   `'tuy-chon'` ngày do `SUPER_ADMIN` gõ (cần cho ô `Q3`)             ⇒ gửi đúng ngày ấy
   * Gộp `'giu'` với `null` là biến một lần sửa số điện thoại thành một lần gia hạn vĩnh viễn.
   */
  type ChonHan = number | null | 'giu' | 'tuy-chon';
  const [goi, setGoi] = useState<ChonHan>(isNew ? null : 'giu');
  const [ngayTuyChon, setNgayTuyChon] = useState('');

  /* ── ④ tuyến ─────────────────────────────────────────────────────────────────────── */
  const [items, setItems] = useState<RouteAccessItem[]>(() => (user?.routeAccess ?? []).map((a) => ({ ...a })));
  const byRoute = useMemo(() => new Map(items.map((i) => [i.routeId, i])), [items]);
  /* `v4` `C` — ô tìm + ba nút lọc. Xem khối vẽ ở mục ④ để biết vì sao đếm luôn hiện. */
  const [timTuyen, setTimTuyen] = useState('');
  const [locTuyen, setLocTuyen] = useState<'tat-ca' | 'da-gan' | 'chua-gan'>('tat-ca');

  /*
   * ═════════════════════════════════════════════════════════════════════════════════════
   * 🔴 `v4` `S3` — ĐANG SỬA **CHÍNH MÌNH**? Bốn trường, không hơn.
   *
   *     duoc  mat khau · ho ten · email · so dien thoai
   *     cam   hang · thoi han · tuyen/co · tick · dang dung
   *
   * ⛔ VÀ FORM PHẢI **KHÔNG GỬI** bốn mục cấm, không chỉ làm mờ chúng: máy chủ đo `!== undefined`
   *   *(gửi là bị chặn, dù không đổi gì)*, nên một ô mờ mà vẫn nằm trong thân yêu cầu sẽ làm
   *   người dùng ăn 403 cho một việc họ không hề định làm. Mờ ở mắt, VẮNG ở dây.
   * ⚠ Kể cả `SUPER_ADMIN` — `admin` tự đặt `isActive = false` là tự khoá mình ra khỏi hệ thống,
   *   và cách chữa duy nhất lúc ấy là một dòng `UPDATE` gõ tay vào CSDL.
   * ═════════════════════════════════════════════════════════════════════════════════════
   */
  const laChinhToi = !isNew && user!.id === me?.id;

  /*
   * ═════════════════════════════════════════════════════════════════════════════════════
   * ➕ `GD-02d` 11/09/2026 — **ĐO CÁI HỘP, KHÔNG ĐO CÁI CỬA SỔ.**
   *
   * 🔴 `GD-02c` sửa lỗi bằng cách hạ ngưỡng `@media` từ 900 xuống 1000 px, và tôi ĐÃ GHI ngay
   *   lúc ấy rằng đó là *"một phép SUY từ bề ngang cửa sổ, không phải một phép ĐO cái cột"*.
   *   Ảnh lee chụp lúc 21:10 cho thấy nó còn tệ hơn thế: hộp rộng ~665 px mà **vẫn hai cột**
   *   — tức là `@media` lồng trong `makeStyles` của Griffel **không được sinh ra**, nên ngưỡng
   *   ấy chưa bao giờ chạy dù ở 900 hay 1000.
   *
   * ⚠ VÀ ĐÂY LÀ BÀI HỌC THẬT, không phải một lỗi thư viện: tôi đã **đo một MẶT CHỮ** (chuỗi
   *   `'@media (max-width: 1000px)'` có mặt trong nguồn) chứ không đo **CÁI NỐI** (cái luật
   *   ấy có thật sự chạm tới cái hộp không). Phép canh `[109]` xanh rực trong khi màn hình vỡ
   *   — đúng cái hình dạng lỗi mà dự án này đã cắt nhiều lần ở chỗ khác, lần này ở CSS.
   * ⇒ Từ `GD-02d`: hộp này **KHÔNG còn một `@media` nào**. Bề ngang đọc bằng `ResizeObserver`
   *   gắn thẳng vào `DialogSurface` — một phép ĐO thật, trên đúng cái vật cần đo.
   *
   * ⛔ `hep === null` = **CHƯA ĐO ĐƯỢC** (lần vẽ đầu, trước khi `ResizeObserver` kịp báo).
   *   Luật số 0: không được đoán. Và ca ấy chọn **MỘT CỘT** — sai theo hướng an toàn: một cột
   *   ở màn rộng chỉ hơi thừa chỗ, hai cột ở màn hẹp là đúng cái ảnh lee vừa gửi.
   * ⚠ `880` chứ không `1000`: nay đo CHÍNH cái hộp, nên con số là bề ngang hộp thật. Hộp 960
   *   (màn rộng) ⇒ hai cột; hộp co lại theo `calc(100vw - 32px)` xuống dưới 880 ⇒ một cột.
   * ═════════════════════════════════════════════════════════════════════════════════════
   */
  const [rongHop, setRongHop] = useState<number | null>(null);
  const voHop = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = voHop.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((mucs) => {
      const w = mucs[0]?.contentRect.width;
      if (typeof w === 'number') setRongHop(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  /** `null` (chưa đo được) ⇒ coi là HẸP — sai theo hướng an toàn. */
  const hep = rongHop === null ? true : rongHop < 880;

  /*
   * ⛔ `v4` `S1` — TICK BỊ KHOÁ THEO BẬC PHẢI **RỜI KHỎI** `tick`, không chỉ mất dấu tích.
   *
   * Ca thật: mở form sửa một tài khoản `MANAGER` mà CSDL còn `tao_tai_khoan` *(hàng cũ, chưa
   * chạy backfill)*. Ô hiện ra khoá và không tích — nhưng nếu chuỗi ấy còn nằm trong `tick`
   * thì lần Lưu kế tiếp vẫn gửi nó đi, và người bấm ăn `403` cho một ô họ **không bấm được**.
   * ⚠ Chạy cả khi đổi hạng lúc TẠO MỚI (Khách hàng → Vận hành): hai ô vừa hợp lệ thành không.
   */
  useEffect(() => {
    setTick((p) => (tickCamTheoHang(role, p).length === 0
      ? p
      : p.filter((k) => tickHopVoiHang(role, k))));
  }, [role]);

  /*
   * ── ⑤ tính năng ──────────────────────────────────────────────────────────────────────
   * 🔴 TẠO MỚI ⇒ ĐÁNH DẤU SẴN BỘ MẶC ĐỊNH CỦA HẠNG (`Q10` phần B, lỗi 2).
   *   Bản trước khởi tạo `[]` cho tài khoản mới, và `[]` là một lời khai THẬT (*"không tick
   *   nào"*) nên máy chủ tôn trọng nó ⇒ Khách hàng tạo ra không có `tao_tai_khoan` ⇒ menu
   *   Quản trị biến mất. Cùng một cái bẫy "thiếu ≠ rỗng", lần này ở phía trình duyệt.
   * ⚠ Và vẫn kẹp theo người tạo: chỉ đánh dấu sẵn những khoá NGƯỜI TẠO có — đánh dấu một ô
   *   người ta không cấp nổi là mời họ bấm Lưu rồi ăn 403.
   */
  const macDinhTheoHang = (vai: Role): string[] =>
    [...(TICK_MAC_DINH[vai] ?? [])].filter((k) => tickChoPhepCua(me, isSuper).includes(k));
  const [tick, setTick] = useState<string[]>(() => (user
    ? (user.quyen ?? []).filter((k) => (KHOA_TICK as readonly string[]).includes(k))
    : macDinhTheoHang(isSuper ? 'ADMIN' : 'MANAGER')));

  /* ── ⑥ đang dùng ─────────────────────────────────────────────────────────────────── */
  const [isActive, setIsActive] = useState(user?.isActive ?? true);

  /*
   * ═════════════════════════════════════════════════════════════════════════════════════
   * ➕ `GD-02b` — LỖI MÁY CHỦ: **MỘT CÂU + MỘT DANH SÁCH CHỖ SAI**, không phải một chuỗi.
   *
   * lee: *"phần thông báo hiện tại phải cuộn mới thấy, nếu không để ý sẽ không thấy được."*
   * Chữa chỗ ĐỨNG (xuống chân hộp cố định) mới là một nửa; nửa kia là chữa cái NÓ NÓI.
   *
   * 🔴 VÀ ĐÂY LÀ CHỖ TÔI SUÝT LÀM SAI. Phiếu viết: *"nếu lỗi chỉ ra tuyến/cờ cụ thể (thông
   *   điệp `KHONG_GAN_DUOC…` có tên tuyến/cờ), tô viền đỏ ô tương ứng"* — đọc thẳng ra thì đó
   *   là *mổ câu chữ của máy chủ để tìm tên tuyến*. Tôi đi đo trước khi viết, và hai điều:
   *     ⓵ `PUT /route-access` trả sẵn **`tuyenThua: string[]`** và
   *        **`coThua: [{ routeId, co: ['canControl'|'canEditSchedule'] }]`** — dữ liệu máy
   *        đọc được, nằm ngay trong thân 403.
   *     ⓶ Câu chữ của nó **không hề chứa tên tuyến** — nó chứa tên hai TÀI KHOẢN. Một bộ mổ
   *        chuỗi ở đây sẽ không bao giờ tìm thấy gì, và nó sẽ **im lặng không tô ô nào**.
   *   ⇒ Đọc `tuyenThua`/`coThua`. Mổ một câu tiếng Việt để tìm dữ liệu là đo MẶT CHỮ chứ
   *     không đo THỨ PHẢI ĐO — đúng cái luật đã cắn dự án này năm lần ở lượt 61.
   *
   * ⚠ `cau` giữ NGUYÊN VĂN câu máy chủ (phiếu: *"không đổi thông điệp máy chủ"*); phần
   *   *"Chưa lưu được."* là chữ của màn hình, in ĐẬM ở trước.
   * ═════════════════════════════════════════════════════════════════════════════════════
   */
  interface LoiLuu {
    /** Nguyên văn câu máy chủ trả về. */
    cau: string;
    /** Tuyến người tạo KHÔNG có — id, không phải tên. */
    tuyenThua: string[];
    /** Cờ thừa trên từng tuyến. */
    coThua: Array<{ routeId: string; co: string[] }>;
  }
  const [loi, setLoi] = useState<LoiLuu | null>(null);

  /*
   * ═════════════════════════════════════════════════════════════════════════════════════
   * 🔴 "BĂNG TẮT KHI NGƯỜI DÙNG SỬA" — ĐO CÁI NỐI, KHÔNG NHỜ 12 CHỖ CÙNG NHỚ.
   *
   * Cách hiển nhiên là thêm `setLoi(null)` vào từng `onChange`. Form này có **mười hai** ô,
   * và cách ấy sinh ra mười hai chỗ phải nhớ — rồi ô thứ mười ba ai đó thêm mai sau sẽ quên,
   * và cái quên ấy **không đỏ ở đâu cả**: băng chỉ lì ra một lúc, và người dùng đọc một câu
   * lỗi đã chết về một ô họ vừa sửa xong.
   *
   * ⇒ Thay bằng một phép SO SÁNH: chụp lại trạng thái form ở đúng lúc lỗi rơi về, rồi băng
   *   chỉ hiện khi trạng thái HIỆN TẠI vẫn y như ảnh chụp ấy. Sửa bất kỳ ô nào — kể cả một ô
   *   thêm vào ngày mai — là ảnh lệch, là băng tắt. Không ô nào phải biết băng tồn tại.
   *
   * ⚠ ĐIỀU KIỆN DUY NHẤT: `anhChup` phải kể ĐỦ mọi ô. Thiếu một ô thì sửa ô ấy không tắt
   *   băng — hỏng về phía "băng lì", đúng cái đang chữa. Nên mục `[100]` của `verify` đối
   *   chiếu danh sách trong `anhChup` với các `useState` của chính hàm này, và đỏ khi lệch.
   * ═════════════════════════════════════════════════════════════════════════════════════
   */
  const anhChup = JSON.stringify({
    username, password, fullName, email, phone,
    role, goi, ngayTuyChon, items, tick, isActive,
  });
  const [anhLucLoi, setAnhLucLoi] = useState<string | null>(null);
  const loiHien = loi && anhLucLoi === anhChup ? loi : null;

  /** Đặt lỗi kèm ảnh chụp — một cửa duy nhất, để hai thứ không bao giờ lệch pha. */
  const datLoi = (v: LoiLuu | null) => {
    setLoi(v);
    setAnhLucLoi(v ? anhChup : null);
  };

  /*
   * 🔴 KHÁCH CHỈ THẤY THỨ MÌNH CÓ — cả tuyến lẫn tick (lee chốt). Đây KHÔNG phải để chặn
   *   (máy chủ mới chặn, bằng `tickThua` và ⊆ tuyến); nó để người bấm không dựng một tài khoản
   *   trong đầu rồi mới biết mình không cấp được — *đừng bày ra thứ sẽ bị từ chối.*
   * ⚠ `SUPER_ADMIN` có `quyen = []` mà vẫn qua mọi cổng ⇒ nó thấy CẢ NĂM. Đọc `[]` của nó
   *   thành "không có gì" là đúng dữ liệu mà sai nghĩa — cùng cái bẫy cờ mốc `CHUA_DIEN`.
   */
  const tickChoPhep = tickChoPhepCua(me, isSuper);
  const tuyenChoPhep = useMemo(() => {
    const ds = routes.data ?? [];
    if (isSuper) return ds;
    const cua = new Set((me?.routeAccess ?? []).map((r) => r.routeId));
    return ds.filter((r) => cua.has(r.id));
  }, [routes.data, isSuper, me]);
  /*
   * ═════════════════════════════════════════════════════════════════════════════════════
   * `v4` `C` — Ô TÌM + BA NÚT LỌC. lee đặt hàng vì danh sách tuyến dài lên theo dự án.
   *
   * 🔴 BA CHỖ DỄ LÀM HỎNG, và lee đã chỉ sẵn hai:
   *   ⓵ **ĐẾM PHẢI LÀ ĐẾM TRÊN TOÀN BỘ**, không phải trên phần đang lọc. Gõ "vo van" mà thấy
   *      *"đã chọn 1 / 1"* thì người ta tưởng bốn tuyến kia vừa bị bỏ chọn — lee nói thẳng:
   *      *"để không tưởng mất tuyến"*. Nên `soDaChon`/`tong` tính từ `tuyenChoPhep`, KHÔNG từ
   *      `tuyenHien`.
   *   ⓶ **LỌC KHÔNG ĐƯỢC ĐỤNG VÀO `items`.** Một ô tìm chỉ thu hẹp cái NHÌN; tuyến đã chọn mà
   *      trôi ra ngoài bộ lọc vẫn còn nguyên trong thân yêu cầu. Ở đây điều đó rơi ra theo cấu
   *      tạo: `items` là trạng thái riêng, `tuyenHien` chỉ là một mảng để vẽ.
   *   ⓷ Không dấu — `khoaTim()` (`lib/khongdau.ts`), dùng chung với tên tệp CSV.
   * ═════════════════════════════════════════════════════════════════════════════════════
   */
  const tuyenHien = useMemo(() => {
    const kw = khoaTim(timTuyen);
    return tuyenChoPhep.filter((r) => {
      if (locTuyen === 'da-gan' && !byRoute.has(r.id)) return false;
      if (locTuyen === 'chua-gan' && byRoute.has(r.id)) return false;
      if (!kw) return true;
      /* Tìm theo TÊN ĐẦY ĐỦ · mã · quận — ba thứ người ta nhớ về một tuyến. */
      return khoaTim(`${r.tenDayDu} ${r.code ?? ''}`).includes(kw);
    });
  }, [tuyenChoPhep, timTuyen, locTuyen, byRoute]);
  const soDaChon = useMemo(
    () => tuyenChoPhep.filter((r) => byRoute.has(r.id)).length, [tuyenChoPhep, byRoute]);

  /** Cờ tối đa khách được cấp trên MỘT tuyến = đúng cờ họ có trên chính tuyến ấy. */
  const coToiDa = (routeId: string) => {
    if (isSuper) return { canControl: true, canEditSchedule: true };
    const r = (me?.routeAccess ?? []).find((x) => x.routeId === routeId);
    return { canControl: !!r?.canControl, canEditSchedule: !!r?.canEditSchedule };
  };

  const toggleRoute = (routeId: string) => {
    setItems((prev) => prev.some((i) => i.routeId === routeId)
      ? prev.filter((i) => i.routeId !== routeId)
      /* Mặc định BẬT/TẮT nếu người tạo có nó, KHÔNG mặc định cấp thứ mình không có. */
      : [...prev, { routeId, canControl: coToiDa(routeId).canControl, canEditSchedule: false }]);
  };
  const patchRoute = (routeId: string, patch: Partial<RouteAccessItem>) =>
    setItems((prev) => prev.map((i) => (i.routeId === routeId ? { ...i, ...patch } : i)));

  /**
   * 🔴 MỐC HẠN TÍNH Ở LÚC BẤM, không ở lúc mở form — một form mở từ sáng mà bấm lúc chiều thì
   *   hai mốc lệch nhau, và đó là loại lệch không ai đi tìm.
   * ⚠ `'giu'` = **không đụng tới hạn** (sửa tài khoản mà không nói gì về thời hạn). Nó KHÁC
   *   `null` (= vĩnh viễn) — hai giá trị, hai nghĩa, và gộp chúng là biến một lần sửa số điện
   *   thoại thành một lần gia hạn vĩnh viễn.
   */
  const tinhHan = (): string | null | undefined => {
    if (goi === 'giu') return undefined;
    if (goi === 'tuy-chon') return ngayTuyChon ? new Date(ngayTuyChon).toISOString() : undefined;
    const d = hanTheoGoi(goi, new Date());
    return d ? d.toISOString() : null;
  };

  const save = useMutation({
    mutationFn: async () => {
      const han = tinhHan();
      if (isNew) {
        const tao = await api.post<{ id: string }>('/api/users', {
          username: username.trim(),
          password,
          fullName: fullName.trim(),
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          role,
          quyen: tick,
          ...(han === undefined ? {} : { expiresAt: han }),
        });
        /*
         * ⚠ HAI LỜI GỌI, và thứ tự bắt buộc: tài khoản phải TỒN TẠI rồi mới gán được tuyến cho
         *   nó. Máy chủ không nhận `routeAccess` trong `POST /users` — cố ý: gán tuyến là một
         *   cổng RIÊNG, và trộn nó vào cửa tạo là mở một đường vòng quanh cổng ấy.
         * ⚠ Chỉ gọi khi CÓ tuyến để gán: một `PUT` với `items: []` là một lệnh **xoá sạch
         *   tuyến**, và gửi nó sau mỗi lần tạo là dựng sẵn một cách xoá nhầm.
         */
        if (items.length > 0) {
          await api.put(`/api/users/${tao.id}/route-access`, { items });
        }
        return tao;
      }
      /*
       * ⛔ `v4` `S5` — **KHÔNG GỬI `role` Ở ĐƯỜNG SỬA**. Máy chủ trả `400 KHONG_DOI_HANG` khi hạng
       *   khác hạng đang có; gửi đúng hạng cũ thì được cho qua, nhưng gửi làm gì một trường
       *   không đổi được? Bỏ hẳn là cách nói *"ô này không phải một ô"* bằng chính cái dây.
       * ⛔ `v4` `S3` — tự sửa thì bốn mục cấm KHÔNG có mặt trong thân yêu cầu.
       */
      const sua = await api.patch(`/api/users/${user!.id}`, {
        fullName: fullName.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        ...(password ? { password } : {}),
        ...(laChinhToi ? {} : {
          isActive,
          quyen: tick,
          ...(han === undefined ? {} : { expiresAt: han }),
        }),
      });
      /*
       * ⚠ Ở đường SỬA thì `items: []` là một ý ĐỊNH THẬT (*"bỏ hết tuyến của tài khoản này"*),
       *   nên ở đây gọi không điều kiện — khác hẳn đường TẠO ngay trên. Hai đường, hai nghĩa
       *   của cùng một mảng rỗng.
       * ⛔ Trừ khi đang sửa CHÍNH MÌNH: máy chủ cấm tự gán tuyến, và gửi đi là ăn 403 cho một
       *   việc người dùng không hề định làm.
       */
      if (!laChinhToi) await api.put(`/api/users/${user!.id}/route-access`, { items });
      return sua;
    },
    onSuccess: () => {
      toast.success(
        isNew ? 'Đã tạo tài khoản' : 'Đã cập nhật tài khoản',
        password ? 'Người dùng sẽ phải đổi mật khẩu ở lần đăng nhập kế tiếp.' : undefined
      );
      qc.invalidateQueries({ queryKey: qk.users });
      onClose();
    },
    /*
     * ⚠ ĐỌC THÂN 403, KHÔNG ĐỌC CÂU CHỮ. `ApiError` giữ nguyên `body` (xem `lib/api.ts` — nó
     *   sinh ra chính vì màn lịch đóng cắt cần mảng `losses` trong 409). Hai mảng dưới đây là
     *   thứ máy chủ đã dựng sẵn; ta chỉ nhận, không suy diễn.
     * ⛔ `Array.isArray` cho từng mảng: một máy chủ cũ hơn không có hai trường ấy thì băng vẫn
     *   hiện CÂU, chỉ là không tô được ô nào — hỏng về phía "nói ít hơn", không phải phía nổ.
     */
    onError: (e) => {
      const b: any = e instanceof ApiError ? e.body : null;
      datLoi({
        cau: errorMessage(e),
        tuyenThua: Array.isArray(b?.tuyenThua) ? b.tuyenThua.map(String) : [],
        coThua: Array.isArray(b?.coThua) ? b.coThua : [],
      });
    },
  });

  /*
   * 🔴 LỖI TỰ KIỂM CŨNG ĐI QUA ĐÚNG BĂNG ẤY. Bốn câu dưới đây là lỗi của TRÌNH DUYỆT, không
   *   phải của máy chủ — nhưng người bấm không phân biệt hai thứ đó, và họ không nên phải
   *   phân biệt. Hai chỗ hiện lỗi là hai chỗ người ta phải học cách nhìn.
   */
  const loiTuKiem = (cau: string) => { datLoi({ cau, tuyenThua: [], coThua: [] }); };

  const submit = () => {
    datLoi(null);
    if (isNew) {
      if (username.trim().length < 3) return loiTuKiem('Tên đăng nhập tối thiểu 3 ký tự');
      if (password.length < 6) return loiTuKiem('Mật khẩu tối thiểu 6 ký tự');
    } else if (password && password.length < 6) {
      return loiTuKiem('Mật khẩu mới tối thiểu 6 ký tự');
    }
    if (!fullName.trim()) return loiTuKiem('Nhập họ tên đầy đủ');
    save.mutate();
  };

  /*
   * ═════════════════════════════════════════════════════════════════════════════════════
   * 🔴 CHỈ RA CHỖ SAI — VÀ PHẢI **GỠ BỘ LỌC** TRƯỚC KHI CHỈ.
   *
   * Đây là cái bẫy tôi chỉ thấy khi đi hết một lượt bằng đầu: bảng tuyến có ô tìm + ba nút
   * lọc (`v4` `C`). Người bấm rất dễ đang gõ "vo van" lúc bấm Lưu. Máy chủ từ chối một tuyến
   * **không nằm trong phần đang hiện** ⇒ hàng ấy KHÔNG được vẽ ⇒ viền đỏ tô vào hư không,
   * `scrollIntoView` cuộn tới `null`, và băng ở chân hộp chỉ tay vào một thứ người dùng
   * không thể nhìn thấy. Một lời hướng dẫn trỏ vào chỗ trống còn tệ hơn không hướng dẫn.
   *
   * ⇒ Lỗi có nêu tuyến ⇒ **gỡ ô tìm và trả bộ lọc về "Tất cả"** rồi mới cuộn. Mất bộ lọc
   *   người dùng đang gõ là một cái giá; không thấy chỗ sai là một ngõ cụt.
   * ⚠ `setTimeout(…, 0)`: phải để React vẽ xong danh sách đầy đủ rồi mới có hàng để cuộn tới.
   * ═════════════════════════════════════════════════════════════════════════════════════
   */
  const tuyenSai = useMemo(() => {
    const t = new Set<string>(loiHien?.tuyenThua ?? []);
    for (const c of loiHien?.coThua ?? []) t.add(c.routeId);
    return t;
  }, [loiHien]);
  const coSai = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const c of loiHien?.coThua ?? []) m.set(c.routeId, new Set(c.co));
    return m;
  }, [loiHien]);

  useEffect(() => {
    if (!loi || tuyenSai.size === 0) return;
    setTimTuyen('');
    setLocTuyen('tat-ca');
    const id = [...tuyenSai][0];
    const t = setTimeout(() => {
      document.getElementById(`hang-tuyen-${id}`)
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 0);
    return () => clearTimeout(t);
  }, [loi]);

  /** Tên đọc được của một tuyến — băng lỗi phải nói TÊN, máy chủ chỉ trả `id`. */
  const tenTuyen = (id: string) =>
    (routes.data ?? []).find((r) => r.id === id)?.tenDayDu ?? id;

  /*
   * ═════════════════════════════════════════════════════════════════════════════════════
   * HUY HIỆU ĐẦU HỘP — *"hạng · tạo bởi @…"* (mẫu `22`). Và cái tên ấy phải ĐI TÌM.
   *
   * ⚠ `UserRow` mang `createdById` (một **id**), không mang `nguoiTao.username` — trường ấy
   *   chỉ có ở `/api/auth/me`. Nên tên người tạo phải tra trong danh sách tài khoản; danh
   *   sách đã nằm sẵn trong bộ nhớ đệm vì trang này vừa vẽ cây từ chính nó (`qk.users`), nên
   *   `useUsers(true)` ở đây **không sinh thêm một lời gọi mạng nào**.
   *
   * ⛔ BA CA, BA CÂU — Luật số 0, và ca giữa là ca người ta hay nuốt mất:
   *     `createdById === null`  → tài khoản GỐC, thật sự không có người tạo
   *     có id, KHÔNG tra ra tên → người tạo nằm NGOÀI tầm nhìn của tôi (tổ tiên, hoặc chính
   *                               tôi) ⇒ **im lặng**, không in gì
   *     tra ra tên             → in `@tên`
   *   Ca giữa mà in `@undefined` hay `@—` là dựng một câu trả lời cho một câu chưa có dữ
   *   liệu; in "tài khoản gốc" cho nó thì còn tệ hơn — đó là nói SAI, không phải nói thiếu.
   * ═════════════════════════════════════════════════════════════════════════════════════
   */
  /*
   * ➕ `GD-05` — CUỘN TỚI Ô MẬT KHẨU khi hộp mở từ nút 🔑.
   *
   * ⚠ `setTimeout(…, 0)`: `DialogSurface` của Fluent gắn vào DOM sau lần vẽ đầu, nên cuộn
   *   ngay trong `useEffect` là cuộn tới một phần tử chưa có chỗ đứng. Cùng cái bẫy đã gặp ở
   *   `GD-02b` khi cuộn tới hàng tuyến bị từ chối.
   * ⛔ `preventScroll: false` — tức là CHO cuộn: cả mục đích của nút 🔑 là đưa người dùng tới
   *   đúng ô ấy. `focus()` mà chặn cuộn thì con trỏ nằm ở một ô ngoài tầm mắt, và người bấm
   *   gõ mật khẩu vào một chỗ họ không nhìn thấy.
   */
  const oMatKhau = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!cuonToiMatKhau) return;
    const t = setTimeout(() => {
      oMatKhau.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      oMatKhau.current?.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [cuonToiMatKhau]);

  const dsTaiKhoan = useUsers(true);
  const nhanNguoiTao = useMemo(() => {
    if (isNew) return '';
    if (user!.createdById === null) return ' · tài khoản gốc';
    const ten = (dsTaiKhoan.data ?? []).find((u) => u.id === user!.createdById)?.username;
    return ten ? ` · tạo bởi @${ten}` : '';
  }, [isNew, user, dsTaiKhoan.data]);

  /*
   * `v4` `S3` — MỘT chỗ dựng lớp khoá cho bốn mục cấm khi tự sửa. Bốn lần gõ lại cùng một câu
   * là bốn câu sẽ trôi khỏi nhau; và câu ấy phải nói ra **đi hỏi ai**, nên nó dùng `BangKhoa`
   * (băng tự lấy tên người cấp quyền từ `/me`).
   */
  /* TK-27 — giữ dữ liệu đang nhập (xem `lib/giunhap.ts`). Đang lưu thì không chặn gì. */
  const [daNhap, setDaNhap] = useState(false);
  const [dangHoi, setDangHoi] = useState(false);
  /*
   * ⚠ Nghe bằng sự kiện GỐC trên vỏ hộp, không bằng `onChangeCapture` trên `DialogSurface`: Fluent
   *   lọc thuộc tính theo danh sách cho phép và lặng lẽ bỏ hai thuộc tính "Capture" ⇒ bản đầu không
   *   bao giờ biết người dùng đã gõ (bộ tự chụp TK-27 bắt được).
   * ⛔ `setTimeout`, KHÔNG gọi thẳng: đặt trạng thái ngay trong lúc nghe sự kiện gốc làm React vẽ lại
   *   GIỮA hai bước của cùng một phím — ô nhập bị trả về giá trị cũ và **mất đúng chữ đầu tiên**
   *   (đo được: gõ "abc" còn "bc"). Lùi một nhịp thì React xử lý xong phím rồi mới tới cờ này.
   */
  useEffect(() => {
    const vo = voHop.current;
    if (!vo || daNhap) return;
    const go = () => { setTimeout(() => setDaNhap(true), 0); };
    vo.addEventListener('input', go, true);
    vo.addEventListener('change', go, true);
    return () => { vo.removeEventListener('input', go, true); vo.removeEventListener('change', go, true); };
  }, [daNhap]);
  const canGiu = daNhap && !save.isPending;
  const xinDong = () => { if (canGiu) setDangHoi(true); else onClose(); };
  useChanKeoTaiLai(true);
  useHoiKhiTaiLai(canGiu);
  useGiuKhiLui(canGiu, () => setDangHoi(true));

  const boc = (cau: string, noiDung: React.ReactNode) =>
    (laChinhToi ? <KhoiKhoa cau={cau}>{noiDung}</KhoiKhoa> : <>{noiDung}</>);

  return (
    /*
     * TK-27 — chạm nền mờ KHÔNG đóng hộp (đây chính là chỗ lee mất dữ liệu trên máy tính bảng:
     *   hộp Fluent mặc định coi chạm nền là "đóng"). Esc khi đã nhập ⇒ hỏi lại, như hộp chung.
     */
    <Dialog open modalType="modal" onOpenChange={(_e, d) => {
      if (d.open || d.type === 'backdropClick') return;
      xinDong();
    }}>
      {/*
        * ═══════════════════════════════════════════════════════════════════════════════
        * ➕ `GD-02b` — HỘP 960 px, HAI CỘT, CHÂN CỐ ĐỊNH. (Mẫu `_BUS/22`.)
        *
        * lee nói hai câu, và chúng là HAI việc khác nhau chứ không phải một:
        *   ⓵ *"phần bố trí này bạn gom gọn lại dễ nhìn hơn"* — một cột dài sáu mục thì
        *      thông tin và tuyến cách nhau ba màn hình cuộn, và người bấm không bao giờ
        *      thấy được **cả hình** của cái tài khoản họ đang dựng.
        *   ⓶ *"phần thông báo hiện tại phải cuộn mới thấy"* — băng lỗi nằm CUỐI vùng cuộn.
        *      Bấm Lưu, không có gì xảy ra trong tầm mắt, người dùng bấm lại.
        *
        * ⚠ THỨ TỰ: ĐÚNG MỘT MỤC ĐỔI CHỖ, và tôi nói thẳng ra chứ không giấu nó sau chữ
        *   *"chỉ đổi cách xếp"*.
        *       cũ:  thông tin → hạng → thời hạn → tuyến → tính năng → **đang dùng**
        *       mới: thông tin → hạng → thời hạn → **đang dùng** → tuyến → tính năng
        *   Phiếu viết *"thứ tự nghĩa không đổi"* ở mục "Không đổi", NHƯNG cùng phiếu ấy đặt
        *   khối 2 là *"Hạng · Thời hạn · Đang dùng"* — hai câu không thể cùng đúng, và **mẫu
        *   `22` là đề bài** nên nó xử. Năm mục còn lại giữ nguyên thứ tự tuyệt đối.
        * 🔴 Và lý do việc đổi ấy ĐƯỢC: ba dòng của khối 2 đều nói về **TƯ CÁCH** của tài
        *   khoản — nó là hạng gì, sống tới bao giờ, còn vào được không. Không dòng nào nói
        *   về nội dung (tên, tuyến, tính năng). Đứng cạnh nhau thì ba dòng đọc ra MỘT câu;
        *   rải "đang dùng" xuống tận cuối form thì người đọc phải tự ghép bằng trí nhớ.
        * ⛔ Mục `[93]` của `verify` ghim thứ tự MỚI, kèm câu giải thích vì sao nó mới — để
        *   lần sau ai đó đảo tiếp thì vẫn đỏ.
        * ═══════════════════════════════════════════════════════════════════════════════
        */}
      <DialogSurface className={s.hopTo} ref={voHop}>
        <DialogBody className={s.thanHop}>
          <DialogTitle>
            <span className="flex flex-wrap items-center gap-2">
              <span className="chu-tieude">
                {isNew ? 'Thêm tài khoản' : `Sửa tài khoản @${user!.username}`}
              </span>
              {/*
                * Huy hiệu "hạng · tạo bởi @…" ngay cạnh tiêu đề (mẫu `22`): hai thứ người
                * đọc cần để biết mình đang đứng ở đâu trong cây, trước khi đọc ô nào.
                * ⚠ Chỉ ở hộp SỬA — một tài khoản chưa tồn tại thì chưa có hạng đã chốt và
                *   chưa có người tạo. Vẽ "tạo bởi @—" là dựng một câu trả lời chưa có.
                */}
              {!isNew && (
                <Badge appearance="tint" color="informative">
                  {tenHang(role)}{nhanNguoiTao}
                </Badge>
              )}
            </span>
          </DialogTitle>
          <DialogContent className={s.vungCuon}>
            <div className={hep ? s.haiCotHep : s.haiCot}>
             <div className={`${hep ? s.cotTraiHep : s.cotTrai} flex flex-col gap-4`}>
              {/* ── ① THÔNG TIN ─────────────────────────────────────────────────── */}
              <Muc so="1" ten="Thông tin">
                {/*
                  * ═══════════════════════════════════════════════════════════════════════
                  * ➕ `GD-02c` — HAI HÀNG × HAI Ô (lỗi 1 lee chụp). Xem khối chú thích ở
                  * `luoiThongTin` để biết vì sao ba ô một hàng KHÔNG cứu được bằng ngưỡng.
                  *
                  * ⚠ THỨ TỰ theo phiếu, và nó khác nhau giữa hai form vì một lý do thật:
                  *     Thêm  — `Tên đăng nhập` đứng TRƯỚC `Họ và tên` (nó là DANH TÍNH, và là
                  *             thứ duy nhất không sửa lại được sau khi tạo), rồi `Email` ·
                  *             `Số điện thoại`, rồi `Mật khẩu tạm` **một hàng riêng full-width**
                  *     Sửa   — `Họ và tên` · `Email`, rồi `Số điện thoại`
                  * ⛔ Ô mật khẩu chiếm CẢ HÀNG (`oCaHang`): nhãn của nó dài nhất hộp
                  *   (*"Đặt lại mật khẩu (bỏ trống nếu không đổi) (hiện rõ để đọc cho khách)"*)
                  *   và câu `hint` dưới nó là một câu đầy đủ. Nhét nó vào nửa hàng là dựng lại
                  *   đúng cái lỗi vừa sửa, ở một ô khác.
                  * ═══════════════════════════════════════════════════════════════════════
                  */}
                <div className={hep ? s.luoiThongTinHep : s.luoiThongTin}>
                  {isNew && (
                    <Field label="Tên đăng nhập">
                      <Input
                        className={s.oNhapDay}
                        value={username}
                        autoCapitalize="none"
                        onChange={(_e, d) => setUsername(d.value)}
                      />
                    </Field>
                  )}
                  <Field label="Họ và tên">
                    <Input className={s.oNhapDay} value={fullName} onChange={(_e, d) => setFullName(d.value)} />
                  </Field>
                  <Field label="Email">
                    <Input className={s.oNhapDay} type="email" value={email} onChange={(_e, d) => setEmail(d.value)} />
                  </Field>
                  <Field label="Số điện thoại">
                    <Input className={s.oNhapDay} value={phone} onChange={(_e, d) => setPhone(d.value)} />
                  </Field>
                <Field
                  className={s.oCaHang}
                  label={
                    <span>
                      {isNew ? 'Mật khẩu tạm' : 'Đặt lại mật khẩu (bỏ trống nếu không đổi)'}
                      <span className={s.mo}> (hiện rõ để đọc cho khách)</span>
                    </span>
                  }
                  hint={
                    <span className="flex items-center gap-1">
                      <Key20Regular />
                      Người dùng bắt buộc đổi mật khẩu ở lần đăng nhập kế tiếp.
                    </span>
                  }
                >
                  {/*
                    * ═══════════════════════════════════════════════════════════════════════
                    * 🔴 `GD-02` LẬT LẠI `GD-01`: ô này là `type="text"`, **hiện rõ**.
                    *
                    * `GD-01` đổi nó sang `password` theo phiếu; tôi báo ngay lúc ấy rằng đó là
                    * chỗ duy nhất của phiếu làm đổi một HÀNH VI, không chỉ đổi dáng. lee nhìn
                    * mẫu và chốt ngược — và anh đúng hơn: **đây không phải ô đăng nhập**. Người
                    * quản trị GÕ RA một mật khẩu tạm rồi phải **đọc nó cho khách qua điện
                    * thoại**. Che nó đi là bắt người ta gõ mù một chuỗi họ sắp phải đọc to.
                    *
                    * ⚠ Và rủi ro "người sau lưng nhìn thấy" gần như bằng không ở đây: chuỗi này
                    *   sống đúng tới lần đăng nhập kế tiếp — máy chủ đặt `mustChangePwd = true`
                    *   cho mọi tài khoản tạo/đặt lại mật khẩu. Nó là một mã dùng một lần, không
                    *   phải một bí mật.
                    * ⛔ Chú thích "(hiện rõ để đọc cho khách)" là BẮT BUỘC, không phải trang trí:
                    *   một ô mật khẩu không che mà không giải thích trông như một lỗi.
                    * ═══════════════════════════════════════════════════════════════════════
                    */}
                  {/*
                    * ═══════════════════════════════════════════════════════════════════════
                    * 🚨 `GD-02b` — VÀ ĐÂY LÀ CHỖ HỎNG THẬT, dev_device chỉ ra: *"ô mật khẩu ở
                    *   hộp Sửa đang hiện `••••` dù nhãn nói hiện rõ."*
                    *
                    * `type` LUÔN LÀ `"text"` — từ `GD-02`, không đổi. Thứ nói dối là
                    * **`placeholder`**: nó là chuỗi `'••••••'` gõ tay. Một ô trống vẽ sáu chấm
                    * tròn thì **không phân biệt được** với một ô mật khẩu đã che, và lee lẫn
                    * dev_device đều đọc ra "ô này đang che".
                    *
                    * 🔴 Và nó đúng một họ với những lỗi nặng nhất của dự án này: `placeholder`
                    *   nói về một giá trị CHƯA CÓ, mà sáu chấm tròn là hình dạng của một giá
                    *   trị ĐÃ CÓ và đang bị giấu. **Một ô rỗng tự vẽ mình như một ô có nội
                    *   dung** — Luật số 0, lần này ở ngay chỗ dễ nhìn nhất.
                    * ⇒ `placeholder` nói đúng cái nó là: *"bỏ trống nếu không đổi"*.
                    * ═══════════════════════════════════════════════════════════════════════
                    */}
                  <Input
                    className={s.oNhapDay}
                    ref={oMatKhau}
                    type="text"
                    value={password}
                    onChange={(_e, d) => setPassword(d.value)}
                    placeholder={isNew ? 'tối thiểu 6 ký tự' : 'bỏ trống nếu không đổi'}
                  />
                </Field>
                </div>
                {/*
                  * ═══════════════════════════════════════════════════════════════════════════
                  * ⛔ Ô "DỰ ÁN" ĐÃ **BỎ** (dev_device chốt 10/09 tối) — và bỏ hẳn, không làm mờ.
                  *
                  * Hệ thống hôm nay chạy **một dự án**. Một ô chọn chỉ có đúng một lựa chọn không
                  * giúp ai: nó chỉ hỏi một câu mà người bấm không có cơ sở nào để trả lời khác đi,
                  * và mỗi câu hỏi thừa trong một form phân quyền là một chỗ bấm nhầm.
                  *
                  * ⚠ MÁY CHỦ MỚI LÀ CHỖ ĐÓNG THẬT: `POST /api/users` nay **ép** `projectId` theo
                  *   người tạo cho MỌI hạng, kể cả `SUPER_ADMIN`, và ghi `PROJECTID_BI_BO_QUA` nếu
                  *   thân yêu cầu vẫn mang một giá trị khác. Ẩn ở form chỉ là thôi bày ra.
                  *
                  * 📌 GIỮ NGUYÊN cột `projectId` trong CSDL và đường `POST /api/projects` — **mở lại
                  *   khi có nhiều khu vực, kèm một màn hình quản lý dự án**.
                  * ═══════════════════════════════════════════════════════════════════════════
                  */}
              </Muc>

              {/* ── ②+③+⑥ HẠNG · THỜI HẠN · ĐANG DÙNG ───────────────────────────── */}
              {/*
                * ═══════════════════════════════════════════════════════════════════════════
                * ➕ `GD-02b` — BA MỤC CŨ (② hạng · ③ thời hạn · ⑥ đang dùng) GOM LÀM MỘT KHỐI,
                *   mỗi mục một **DÒNG** (mẫu `22`): nhãn + câu giải thích bên trái, điều khiển
                *   bên phải.
                *
                * 🔴 Vì sao gom được mà không mất ý: ba câu này đều nói về **TƯ CÁCH** của tài
                *   khoản — nó là hạng gì, sống tới bao giờ, còn vào được không. Không câu nào
                *   nói về nội dung (tên, tuyến, tính năng). Đứng cạnh nhau thì đọc ra MỘT câu;
                *   rải ra đầu form và cuối form thì người đọc phải tự ghép lại bằng trí nhớ.
                * ⛔ Và thứ tự NGHĨA không đổi: hạng trước thời hạn, thời hạn trước tuyến. Phiếu
                *   chốt *"chỉ đổi cách xếp, không đổi ý"* — "đang dùng" xuống dòng thứ ba trong
                *   khối chứ không nhảy lên trước hạng.
                * ⚠ HẠNG KHÔNG ĐỔI ĐƯỢC ⇒ **một dòng có ổ khoá**, không phải một khối chữ ba
                *   dòng như trước. Câu dài đầy đủ chuyển xuống `small` — nó vẫn phải còn (nó
                *   trả lời *"thế muốn đổi thì làm sao"*), chỉ thôi chiếm chỗ của một tiêu đề.
                * ═══════════════════════════════════════════════════════════════════════════
                */}
              <Muc so="2" ten="Hạng · thời hạn · đang dùng">
                {!isNew ? (
                  <div className={s.dong}>
                    {/*
                      * ⚠ `GD-02c` — `<span className={s.chuDong}>` thay cho `min-w-0` +
                      *   `Caption1 block`: bố cục do `makeStyles` lo, không do một lớp Tailwind
                      *   phải thắng lớp Griffel của Fluent (xem chú thích ở `dong`).
                      */}
                    <span className={s.chuDong}>
                      <span className={s.tieuDeDong}>Hạng: {tenHang(role)}</span>
                      <span className={s.giaiThichDong}>
                        Không đổi được sau khi tạo — hạng quyết định chỗ đứng của tài khoản trong
                        cây phân quyền; cần đổi thì tạo tài khoản mới rồi xoá tài khoản này.
                      </span>
                    </span>
                    {/*
                      * ⚠ Ổ KHOÁ XÁM, KHÔNG phải `HuyHieuKhoa` hổ phách: hổ phách ở dự án này
                      *   nghĩa là *"bạn thiếu quyền, đi hỏi người cấp"* (mục `[95]`). Hạng thì
                      *   **không ai** đổi được, kể cả `admin` — không có ai để hỏi. Mượn màu ở
                      *   đây là làm hỏng đúng cái phân biệt `[95]` vừa dựng.
                      */}
                    <LockClosed16Regular className={`${s.mo} ${s.dkDong}`} aria-label="Không đổi được" />
                  </div>
                ) : isSuper ? (
                  <Field label="Hạng tài khoản">
                    <Dropdown
                      value={tenHang(role)}
                      selectedOptions={[role]}
                      /*
                       * ⚠ Đổi hạng thì ÁP LẠI bộ mặc định — nhưng CHỈ khi đang TẠO MỚI. Ở màn
                       *   sửa, tick là thứ người ta đã cân nhắc; đạp lên nó vì một cú đổi hạng
                       *   là xoá một quyết định của người khác mà không hỏi.
                       */
                      onOptionSelect={(_e, d) => {
                        const v = (d.optionValue ?? role) as Role;
                        setRole(v);
                        if (isNew) setTick(macDinhTheoHang(v));
                      }}
                    >
                      {HANG_HIEN.map((h) => (
                        <Option key={h.vai} value={h.vai} text={h.ten}>
                          <span className="flex flex-col">
                            <Text weight="semibold">{h.ten}</Text>
                            <Caption1 className={s.mo}>{h.mo}</Caption1>
                          </span>
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>
                ) : (
                  <div className={s.dong}>
                    <span className={s.chuDong}>
                      <span className={s.tieuDeDong}>Hạng: Vận hành</span>
                      <span className={s.giaiThichDong}>
                        Tài khoản bạn tạo luôn là tài khoản vận hành trong phạm vi của bạn (chỉ
                        quản trị hệ thống mới tạo được hạng Khách hàng). Vận hành là <b>bậc thấp
                        nhất</b>: nó không tạo được tài khoản nào nữa.
                      </span>
                    </span>
                    <LockClosed16Regular className={`${s.mo} ${s.dkDong}`} aria-label="Không chọn được" />
                  </div>
                )}

                {/*
                  * ═══════════════════════════════════════════════════════════════════════
                  * ➕ `GD-02d` mục 4 — TỰ SỬA CHÍNH MÌNH: **MỘT băng ngắn**, rồi ẨN HẲN hai
                  * dòng Thời hạn và Đang dùng.
                  *
                  * 🔴 ĐÂY LÀ NGOẠI LỆ DUY NHẤT của luật "khoá thì HIỆN, đừng ẩn", và lý do phải
                  *   nói rõ để không ai mở rộng nó: luật ấy sinh ra cho ca **khoá VÌ QUYỀN** —
                  *   ô có nghĩa, người khác dùng được, tôi thì chưa; ẩn đi là giấu mất sự tồn
                  *   tại của một thứ người ta có thể đi XIN. Ca ở đây khác hẳn: với chính mình,
                  *   hai ô ấy **không có nghĩa nào cả** — không ai tự gia hạn cho mình, và tự
                  *   khoá mình là mất chìa (`v4 S3`). Không có gì để đi xin, nên không có gì để
                  *   giấu.
                  * ⚠ VÀ VẪN PHẢI NÓI RA MỘT CÂU — băng ngắn ở đầu khối. Ẩn trong im lặng thì
                  *   người dùng tưởng hộp bị thiếu; một câu thì họ biết vì sao và đi hỏi đúng
                  *   người. (Tài khoản gốc `admin` không có người tạo, nên câu viết chung chung
                  *   "do người tạo quản" chứ không nêu tên ai.)
                  * ⛔ Hai băng `KhoiKhoa` cũ (mỗi dòng một băng, mỗi băng ba dòng chữ) chiếm chỗ
                  *   hơn cả điều khiển — đúng bệnh "thứ KHÔNG dùng được to hơn thứ dùng được"
                  *   đã cắt ở `GD-02b` cho các ô tính năng, tái diễn ở khối 2.
                  * ═══════════════════════════════════════════════════════════════════════
                  */}
                {laChinhToi && (
                  <BangKhoa cau={'Tài khoản của bạn: thời hạn và trạng thái do người tạo quản; '
                    + 'ở đây chỉ đổi được thông tin và mật khẩu.'} nguoiCap={null} />
                )}

                {/* ── dòng 2 · THỜI HẠN ─────────────────────────────────────────── */}
                {!laChinhToi && (<>
                  <div className={s.dong}>
                    <span className={s.chuDong}>
                      <span className={s.tieuDeDong}>Thời hạn</span>
                      <span className={s.giaiThichDong}>
                        {!isSuper
                          ? 'Không vượt quá hạn của người tạo — máy chủ tự kẹp lại nếu vượt.'
                          : 'Trống = vĩnh viễn. "Giữ nguyên" là không đụng tới hạn đang có.'}
                        {!isNew && user?.expiresAt && (
                          <> Hạn hiện tại: <b>{fmtDate(user.expiresAt)}</b> —{' '}
                            {docHan(user.expiresAt, Date.now()).cau}.</>
                        )}
                      </span>
                    </span>
                    {/*
                      * ⛔ MÃ HOÁ GIÁ TRỊ RA CHUỖI — và đây là Luật số 0 lần nữa, lần này ở một
                      *   chỗ rất dễ trượt: `Dropdown` của Fluent chỉ nói chuyện bằng `string`,
                      *   mà bốn giá trị của ta gồm cả `null` (**VĨNH VIỄN — một mốc thật**) lẫn
                      *   `'giu'` (*"không nói gì về thời hạn"*). Để `null` tự hoá thành `''` là
                      *   để hai câu ấy trùng nhau, và một lần sửa số điện thoại thành một lần
                      *   gia hạn vĩnh viễn.
                      * ⇒ `null` có một mã RIÊNG (`'vinh-vien'`), và hai hàm thuần ở cuối tệp
                      *   dịch qua lại. Không chỗ nào khác được tự dịch.
                      */}
                    <Dropdown
                      className={s.oChonHan}
                      value={nhanHan(goi, isNew)}
                      selectedOptions={[maHan(goi)]}
                      onOptionSelect={(_e, d) => setGoi(docMaHan(d.optionValue ?? maHan(goi)))}
                    >
                      {!isNew && <Option value="giu" text="Giữ nguyên">Giữ nguyên</Option>}
                      {GOI_HAN.map((g) => (
                        <Option key={g.ten} value={maHan(g.thang)} text={g.ten}>{g.ten}</Option>
                      ))}
                      {/*
                        * ⚠ Ô NGÀY TUỲ Ý CHỈ CHO `SUPER_ADMIN` (lee chốt) — và nó có một việc
                        *   thật ngoài sự tiện: ô nghiệm thu `Q3` cần đặt một mốc **trong quá
                        *   khứ** để đo, và không gói nào làm được điều đó.
                        */}
                      {isSuper && <Option value="tuy-chon" text="Ngày tuỳ ý">Ngày tuỳ ý</Option>}
                    </Dropdown>
                  </div>
                  {isSuper && goi === 'tuy-chon' && (
                    <Field label="Ngày hết hạn">
                      <Input
                        type="datetime-local"
                        value={ngayTuyChon}
                        onChange={(_e, d) => setNgayTuyChon(d.value)}
                      />
                    </Field>
                  )}
                </>)}

                {/* ── dòng 3 · ĐANG DÙNG ────────────────────────────────────────── */}
                {/* ⛔ Ẩn khi tự sửa — xem khối chú thích ở dòng 2. Vế `!isNew` giữ nguyên. */}
                {/*
                  * ⛔ `v4` `S3` — **KHOÁ CHÍNH MÌNH LÀ MẤT CHÌA.** Đây là ô nguy nhất trong bốn
                  *   ô cấm: tắt nó cho chính mình thì `biChan()` chặn ngay ở `napTaiKhoan`,
                  *   trước mọi cửa — không còn màn hình nào để bấm lại. Cách chữa duy nhất là
                  *   một dòng `UPDATE` gõ tay vào CSDL, và điều đó đúng **kể cả với `admin`**.
                  * ⚠ CHỈ Ở HỘP SỬA: một tài khoản chưa tồn tại thì luôn được tạo ra ở trạng
                  *   thái đang dùng — hỏi trước là hỏi một câu chưa có nghĩa.
                  */}
                {!isNew && !laChinhToi && (
                  <div className={s.dong}>
                    <span className={s.chuDong}>
                      <span className={s.tieuDeDong}>Đang được phép đăng nhập</span>
                      <span className={s.giaiThichDong}>
                        Tắt là khoá <b>cả nhánh dưới</b> tài khoản này, ngay lập tức.
                      </span>
                    </span>
                    <Switch
                      className={s.dkDong}
                      checked={isActive}
                      onChange={(_e, d) => setIsActive(d.checked)}
                      aria-label="Tài khoản đang được phép đăng nhập"
                    />
                  </div>
                )}
              </Muc>
             </div>

             {/* ══════════ CỘT PHẢI · TUYẾN + TÍNH NĂNG ══════════ */}
             <div className={`${hep ? s.cotPhaiHep : s.cotPhai} flex flex-col gap-4`}>
              {/* ── ③ TUYẾN ─────────────────────────────────────────────────────── */}
              <Muc so="3" ten="Tuyến">
                {!isSuper && (
                  /*
                   * ⚠ Đây KHÔNG phải một khối bị khoá vì quyền — nó là lời giải thích PHẠM VI
                   *   (bạn gán được, chỉ trong phần của bạn). Nên nó dùng nền trung tính, KHÔNG
                   *   mượn màu hổ phách của `<Khoa>`: mượn màu là làm hỏng đúng cái phân biệt
                   *   vừa dựng.
                   */
                  <div className={s.ghiChu}>
                    <Caption1>
                      Chỉ hiện những tuyến <b>bạn đang có</b>, và mỗi tuyến chỉ mở đúng những cờ
                      bạn có trên chính nó. Bạn <b>không tự thêm tuyến cho tài khoản của mình</b> —
                      tuyến của bạn do quản trị viên cấp.
                    </Caption1>
                  </div>
                )}
                {boc('Bạn không tự gán tuyến cho tài khoản của mình — tuyến của bạn do người cấp quyền giao.', <>
                  {routes.isLoading && <Spinner size="tiny" label="Đang tải tuyến…" />}
                  {tuyenChoPhep.length === 0 && !routes.isLoading && (
                    <Text>Bạn chưa được gán tuyến nào.</Text>
                  )}

                  {/* ── `v4` `C` · Ô TÌM · BA NÚT LỌC · ĐẾM ───────────────────────── */}
                  {tuyenChoPhep.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <SearchBox
                        value={timTuyen}
                        onChange={(_e, d) => setTimTuyen(d.value)}
                        placeholder="Tìm theo tên · mã · phường (gõ không dấu cũng được)"
                      />
                      <div className="flex flex-wrap items-center gap-2">
                        {([
                          ['tat-ca', 'Tất cả', tuyenChoPhep.length],
                          ['da-gan', 'Đã gán', soDaChon],
                          ['chua-gan', 'Chưa gán', tuyenChoPhep.length - soDaChon],
                        ] as const).map(([gt, nhan, n]) => (
                          <ToggleButton
                            key={gt}
                            size="small"
                            checked={locTuyen === gt}
                            onClick={() => setLocTuyen(gt)}
                          >
                            {nhan} ({n})
                          </ToggleButton>
                        ))}
                        {/*
                          * ⛔ ĐẾM TRÊN TOÀN BỘ, KHÔNG TRÊN PHẦN ĐANG LỌC (lee: *"để không tưởng
                          *   mất tuyến"*). Và khi bộ lọc đang giấu bớt thì NÓI RA con số bị giấu
                          *   — một danh sách ngắn đi mà không giải thích là một danh sách trông
                          *   như vừa mất dữ liệu.
                          */}
                        <Caption1 className={`${s.mo} ml-auto`}>
                          đã chọn <b>{soDaChon}</b> / {tuyenChoPhep.length}
                          {tuyenHien.length !== tuyenChoPhep.length && (
                            <> · đang hiện {tuyenHien.length}</>
                          )}
                        </Caption1>
                      </div>
                      {tuyenHien.length === 0 && (
                        <Text>
                          Không tuyến nào khớp.{' '}
                          {soDaChon > 0 && (
                            <>Bạn vẫn đang giữ <b>{soDaChon}</b> tuyến đã chọn — bộ lọc chỉ giấu
                            bớt, không bỏ chọn.</>
                          )}
                        </Text>
                      )}
                    </div>
                  )}

                  {/*
                    * ⚠ CUỘN TRONG KHUNG KHI > 8 TUYẾN (lee). Không đặt `max-height` cố định cho
                    *   mọi ca: với 3 tuyến thì một khung cuộn rỗng nửa dưới trông như đang tải dở.
                    */}
                  {tuyenHien.length > 0 && (
                    <div className={tuyenHien.length > 8 ? 'max-h-[22rem] overflow-y-auto pr-1' : ''}>
                      {/*
                        * ═══════════════════════════════════════════════════════════════════
                        * ➕ `GD-02b` — MỘT DÒNG MỖI TUYẾN, `table-layout: fixed`, KHÔNG cuộn ngang.
                        *
                        * Bản `GD-02` xếp tên tuyến và mã/số tủ thành HAI dòng trong một ô, và ba
                        * cột tick để Fluent tự chia. Hệ quả ở hộp 960 px: mỗi tuyến cao gấp đôi,
                        * bốn tuyến đã hết tầm mắt; và một tên dài **đẩy rộng cả bảng** (đó là
                        * cách `table-layout: auto` làm việc) ⇒ thanh cuộn ngang, chữ bị cắt.
                        *
                        * ⛔ HAI THỨ PHẢI ĐI CÙNG NHAU hoặc không thứ nào chạy: `tableLayout:
                        *   'fixed'` (bề rộng do CỘT quyết định) + `text-overflow: ellipsis` (nội
                        *   dung tự cắt). Chỉ có cái thứ hai thì nó **không bao giờ kích hoạt**,
                        *   vì ô không bao giờ hẹp hơn nội dung.
                        * 🔴 VÀ CẮT CHỮ THÌ PHẢI CÓ ĐƯỜNG ĐỌC LẠI: `title={r.tenDayDu}` mang tên
                        *   ĐẦY ĐỦ. Ô phân quyền là chỗ hiểm nhất để hiện một cái tên cụt — tick
                        *   nhầm một *"Nguyễn Huệ…"* là trao quyền điều khiển một tuyến khác hẳn.
                        * ═══════════════════════════════════════════════════════════════════
                        */}
                      <div role="table" aria-label="Tuyến được gán">
                        {/* Hàng TIÊU ĐỀ dùng CHÍNH cái lưới của hàng dữ liệu ⇒ cột không lệch được. */}
                        <div role="row" className={`${s.hangTuyen} ${hep ? s.hangTuyenHep : ''} ${s.dauTuyen}`}>
                          <span role="columnheader" className={s.cotTen}>Tuyến</span>
                          <span role="columnheader" className={s.oTick}>Xem</span>
                          <span role="columnheader" className={s.oTick}>Bật/tắt</span>
                          <span role="columnheader" className={s.oTick}>Đặt lịch</span>
                        </div>
                        {tuyenHien.map((r) => {
                          const item = byRoute.get(r.id);
                          const checked = !!item;
                          const toiDa = coToiDa(r.id);
                          /*
                           * Ô nào máy chủ vừa từ chối — đọc từ `tuyenThua`/`coThua`, KHÔNG mổ
                           * câu chữ. `saiTuyen` = cả tuyến không được phép; `saiCo` = tuyến thì
                           * được, một cờ cụ thể thì không.
                           */
                          const saiTuyen = (loiHien?.tuyenThua ?? []).includes(r.id);
                          const saiCo = coSai.get(r.id);
                          return (
                            <div
                              role="row"
                              key={r.id}
                              id={`hang-tuyen-${r.id}`}
                              className={`${s.hangTuyen} ${hep ? s.hangTuyenHep : ''}`}
                            >
                              {/*
                                * 🔴 CẮT CHỮ THÌ PHẢI CÓ ĐƯỜNG ĐỌC LẠI: `title={r.tenDayDu}` mang
                                *   tên ĐẦY ĐỦ. Ô phân quyền là chỗ hiểm nhất để hiện một cái tên
                                *   cụt — tick nhầm một *"Nguyễn Huệ…"* là trao quyền điều khiển
                                *   một tuyến đường khác hẳn.
                                */}
                              {/*
                                * ⛔ `<span>` THƯỜNG, KHÔNG `<Text>`/`<Caption1>` CỦA FLUENT — và
                                *   ảnh tự chụp lần đầu lòi ra đúng lý do: `Text` của Fluent mang
                                *   lớp Griffel đặt `white-space` CỦA CHÍNH NÓ, và lớp trên con
                                *   thì thắng `nowrap` của cha. Tên tuyến vẫn xuống ba dòng dù ô
                                *   đã `nowrap` + `ellipsis`.
                                * 🔴 CÙNG MỘT BÀI HỌC BA LẦN LIÊN TIẾP: `GD-02c` là `block` của
                                *   Tailwind thua lớp Griffel; `GD-02d` là `maxWidth` mặc định của
                                *   `DialogSurface` thắng `width` của tôi; và đây là lớp của con
                                *   thắng lớp của cha. **Một thuộc tính chỉ chắc chắn ăn khi nó
                                *   nằm trên ĐÚNG cái thẻ mang chữ.**
                                */}
                              <span role="cell" className={`${s.cotTen} ${s.tenMotDong}`} title={r.tenDayDu}>
                                <span className={s.tenTuyenDam}>{r.tenDayDu}</span>
                                <span className={s.tenTuyenPhu}>
                                  {' '}· {r.code ? `${r.code} · ` : ''}{r._count?.cabinets ?? 0} tủ
                                </span>
                              </span>
                              <span role="cell" className={s.oTick}>
                                <span className={saiTuyen ? s.oSai : undefined}>
                                  <Checkbox
                                    checked={checked}
                                    onChange={() => toggleRoute(r.id)}
                                    aria-label={`Gán tuyến ${r.tenDayDu}`}
                                  />
                                </span>
                              </span>
                              <span role="cell" className={s.oTick}>
                                <span
                                  className={`inline-flex items-center gap-1 ${saiCo?.has('canControl') ? s.oSai : ''}`}
                                >
                                  <Checkbox
                                    /* ⛔ Khoá đúng cái cờ NGƯỜI TẠO không có — không khoá cả
                                       hộp. Đây là vế ⊆ của `Q2` vẽ ra thành ô bấm; máy chủ
                                       vẫn kiểm lại độc lập. */
                                    disabled={!checked || !toiDa.canControl}
                                    checked={!!item?.canControl}
                                    onChange={(_e, d) => patchRoute(r.id, { canControl: d.checked === true })}
                                    aria-label={`Bật/tắt trên ${r.tenDayDu}`}
                                  />
                                  {/* Khoá vì NGƯỜI TẠO không có cờ ấy trên chính tuyến này
                                      (vế ⊆ của `Q2`) — cùng màu, cùng ổ khoá với mọi chỗ khoá
                                      vì quyền khác. */}
                                  {!toiDa.canControl && <HuyHieuKhoa ten={NHAN_CO_TUYEN.canControl} />}
                                </span>
                              </span>
                              <span role="cell" className={s.oTick}>
                                <span
                                  className={`inline-flex items-center gap-1 ${saiCo?.has('canEditSchedule') ? s.oSai : ''}`}
                                >
                                  <Checkbox
                                    disabled={!checked || !toiDa.canEditSchedule}
                                    checked={!!item?.canEditSchedule}
                                    onChange={(_e, d) => patchRoute(r.id, { canEditSchedule: d.checked === true })}
                                    aria-label={`Đặt lịch trên ${r.tenDayDu}`}
                                  />
                                  {!toiDa.canEditSchedule && <HuyHieuKhoa ten={NHAN_CO_TUYEN.canEditSchedule} />}
                                </span>
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>)}
              </Muc>

              {/* ── ④ TÍNH NĂNG ─────────────────────────────────────────────────── */}
              <Muc so="4" ten="Tính năng">
                {/*
                  * ═══════════════════════════════════════════════════════════════════════
                  * 🔴 `v4` `S1` — HAI Ô KHOÁ THEO **BẬC CỦA TÀI KHOẢN ĐANG SOẠN**, không theo
                  *   bậc người bấm. Đây là cửa sau lee tìm ra, vẽ ra thành hai ô khoá:
                  *
                  *     khach_hang CO `tao_tai_khoan`  ->  ⊆ thoa  ->  cap cho van hanh
                  *       -> van hanh qua cong requireQuyen('tao_tai_khoan')
                  *          -> tao ra BAC THU TU, thu ma dac ta noi khong ton tai
                  *
                  * ⛔ Và ô bị khoá vẫn **HIỆN**, kèm chữ *"không dành cho bậc Vận hành"* — không
                  *   ẩn. Ẩn thì người cấp quyền không biết tính năng ấy tồn tại, nên cũng không
                  *   hiểu vì sao tài khoản vận hành của họ không tạo được tài khoản phụ. Một ô
                  *   khoá là một câu trả lời; một khoảng trống thì không nói gì (lee chốt 57).
                  * ⚠ VÀ TICK BỊ KHOÁ PHẢI RỜI KHỎI `tick` — xem `useEffect` ở trên.
                  * ═══════════════════════════════════════════════════════════════════════
                  */}
                {boc('Bạn không tự cấp thêm tính năng cho tài khoản của mình.', <>
                  {/*
                    * ⚠ NÓI RA KHOÁ LUÔN-CÓ **TRƯỚC** danh sách, không sau. Người bấm thấy 3 ô và
                    *   kết luận *"bỏ hết tick = tài khoản không xem được gì"* — sai; câu đính
                    *   chính ấy phải tới trước lúc họ kịp kết luận, không phải sau.
                    */}
                  <Caption1 className={s.mo}>
                    Xem trạng thái · cảnh báo · sức khoẻ tủ thì <b>tài khoản nào cũng có</b>, không
                    cần tick.
                  </Caption1>
                  {/*
                    * ═══════════════════════════════════════════════════════════════════════
                    * ➕ `GD-02b` — Ô CHO PHÉP: LƯỚI 2 CỘT, MỖI Ô **MỘT DÒNG**.
                    * Ô BỊ KHOÁ THEO HẠNG: rời khỏi lưới, gom vào **MỘT** băng cam bên dưới.
                    *
                    * Bản `GD-02` bọc từng ô khoá trong một `<Khoa>` riêng — ba khối hổ phách
                    * chồng nhau, mỗi khối ba dòng chữ, và chúng chiếm nhiều chỗ hơn cả những ô
                    * còn bấm được. Một màn hình mà thứ KHÔNG dùng được to hơn thứ dùng được thì
                    * đọc ra là "màn hình này toàn cấm".
                    *
                    * 🔴 NHƯNG GOM THÌ PHẢI GOM ĐÚNG BỞI CÁI GÌ. Ở đây có HAI lý do một tính năng
                    *   không bấm được, và chúng **không phải một**:
                    *     (a) không dành cho HẠNG của tài khoản đang soạn  → nâng hạng cũng không
                    *         được, vì hạng không đổi được; đường ra là người cấp quyền cấp ở tài
                    *         khoản BẬC TRÊN
                    *     (b) chính NGƯỜI BẤM không có tính năng ấy         → đi xin là đúng đường
                    *   ⇒ HAI băng, mỗi băng MỘT. Nhập chúng lại thành một băng "cho gọn" là nói
                    *     với người đọc rằng (a) xin là được — và họ sẽ đi xin một thứ không ai
                    *     cấp nổi. *(Luật: một danh sách đầy đủ cho một câu hỏi không phải là một
                    *     danh sách đầy đủ cho câu hỏi bên cạnh.)*
                    * ⚠ `BangKhoa` vẫn là nguồn vẽ duy nhất (`Khoa.tsx`), và nó TỰ lấy tên người
                    *   cấp quyền từ `/me` — chỗ gọi không có gì để quên.
                    * ═══════════════════════════════════════════════════════════════════════
                    */}
                  <div className={hep ? s.luoiTinhNangHep : s.luoiTinhNang}>
                    {tickChoPhep.filter((k) => tickHopVoiHang(role, k)).map((k) => (
                      <Checkbox
                        key={k}
                        checked={tick.includes(k)}
                        onChange={(_e, d) => setTick((p) => (d.checked === true
                          ? [...p, k]
                          : p.filter((x) => x !== k)))}
                        label={<Text weight="semibold" title={GIAI_THICH_KHOA[k]}>{NHAN_KHOA[k]}</Text>}
                      />
                    ))}
                  </div>
                  {/*
                    * (a) — khoá theo HẠNG của tài khoản đang soạn.
                    *
                    * ⚠ CÂU DO `cauTickKhongDanhChoHang()` DỰNG, tôi không gõ lại. Bản đầu tôi
                    *   viết thẳng `Không dành cho bậc ${tenHang(role)}: A · B` — gọn hơn, và
                    *   **sai về cấu tạo**: dự án đang có HAI bảng tên hạng chạy song song
                    *   (`TEN_BAC` mà hàm kia đọc, và `HANG_HIEN`/`tenHang()` mà huy hiệu đầu
                    *   hộp đọc). Hôm nay chúng trùng nhau; trùng nhau HÔM NAY không phải là
                    *   cùng một nguồn. Gõ câu ấy lần thứ ba là dựng chỗ thứ ba để trôi.
                    * 📌 Hai bảng tên hạng là một chỗ đáng gộp — nhưng đó là việc của một phiếu
                    *   khác, không phải của một phiếu đổi bố cục. Ghi lại ở đây để nó không mất.
                    */}
                  {tickChoPhep.some((k) => !tickHopVoiHang(role, k)) && (
                    <BangKhoa
                      cau={`${tickChoPhep.filter((k) => !tickHopVoiHang(role, k))
                        .map((k) => cauTickKhongDanhChoHang(role, k)).join(' · ')}. `
                         + 'Hạng không đổi được, nên muốn cấp thì cấp ở tài khoản bậc trên.'}
                    />
                  )}
                  {tickChoPhep.length === 0 && (
                    <Text>Bạn không có tính năng nào để cấp lại.</Text>
                  )}
                  {/*
                    * (b) — 🔴 NÓI RA NHỮNG Ô **KHÔNG CÓ Ở ĐÂY**, thay vì để một danh sách ngắn tự
                    *   nói. Người bấm không đếm được cái vắng mặt: họ thấy 3 ô và tưởng hệ thống
                    *   có 3.
                    */}
                  {!isSuper && tickChoPhep.length < KHOA_TICK.length && (
                    <BangKhoa
                      cau={`Bạn không cấp được ${KHOA_TICK.length - tickChoPhep.length} tính năng mà chính bạn `
                         + `không có: ${KHOA_TICK.filter((k) => !tickChoPhep.includes(k)).map((k) => NHAN_KHOA[k]).join(' · ')}.`}
                    />
                  )}
                </>)}
              </Muc>
             </div>
            </div>
          </DialogContent>
          {/*
            * ═══════════════════════════════════════════════════════════════════════════════
            * 🚨 CHÂN HỘP CỐ ĐỊNH — VÀ BĂNG LỖI NẰM Ở ĐÂY, cạnh nút Lưu. Đây là câu thứ hai
            *   của lee: *"phải cuộn mới thấy, nếu không để ý sẽ không thấy được."*
            *
            * Bản cũ đặt băng lỗi ở CUỐI vùng cuộn. Người bấm ở giữa form, bấm Lưu, và câu trả
            * lời rơi xuống một chỗ cách đó hai màn hình. Với họ thì **không có gì xảy ra cả** —
            * nên họ bấm lại, rồi bấm lại. Một thông báo ngoài tầm mắt không phải một thông báo.
            *
            * ⛔ CHỖ ĐỨNG LÀ MỘT NỬA, CÂU CHỮ LÀ NỬA KIA: bắt đầu bằng **"Chưa lưu được."** in
            *   đậm. Một câu lỗi mà không nói ngay rằng *việc đã không xảy ra* thì người đọc phải
            *   suy ra điều đó — và lúc vội thì người ta không suy, người ta đóng hộp.
            * ═══════════════════════════════════════════════════════════════════════════════
            */}
          <DialogActions className={s.chanHop}>
            {loiHien && (
              <MessageBar intent="error" className={s.bangLoi}>
                <MessageBarBody className={s.loiXuongDong}>
                  <b>Chưa lưu được.</b> {loiHien.cau}
                  {/*
                    * ⚠ NÓI TÊN TUYẾN, không nói `id`. Máy chủ trả `tuyenThua`/`coThua` bằng id
                    *   (đúng — nó không biết người đọc), nên chỗ này phải tra ra tên. Một câu
                    *   lỗi chứa `cmf3k9…` là một câu lỗi người dùng không làm gì được.
                    */}
                  {tuyenSai.size > 0 && (
                    <> Chỗ bị từ chối:{' '}
                      <b>
                        {[...tuyenSai].map((id) => {
                          const co = coSai.get(id);
                          const ten = tenTuyen(id);
                          return co && co.size > 0
                            ? `${ten} · ${[...co].map((c) => NHAN_CO_TUYEN[c as 'canControl' | 'canEditSchedule'] ?? c).join(' · ')}`
                            : ten;
                        }).join(' — ')}
                      </b>
                      . Bỏ tick ở những ô viền đỏ rồi lưu lại.
                    </>
                  )}
                </MessageBarBody>
              </MessageBar>
            )}
            {dangHoi && (
              <MessageBar intent="warning" className={s.bangLoi} data-hoi-bo-nhap>
                <MessageBarBody className={s.loiXuongDong}>
                  <b>{CAU_HOI_BO_NHAP.dau}</b> {CAU_HOI_BO_NHAP.than}
                </MessageBarBody>
                <MessageBarActions>
                  <Button appearance="primary" onClick={() => setDangHoi(false)}>{CAU_HOI_BO_NHAP.giu}</Button>
                  <Button onClick={() => { setDangHoi(false); onClose(); }}>{CAU_HOI_BO_NHAP.bo}</Button>
                </MessageBarActions>
              </MessageBar>
            )}
            <Button appearance="secondary" onClick={onClose} disabled={save.isPending}>Huỷ</Button>
            <Button
              appearance="primary"
              disabled={save.isPending}
              icon={save.isPending ? <Spinner size="tiny" /> : <PersonAdd20Regular />}
              onClick={submit}
            >
              {isNew ? 'Tạo tài khoản' : 'Lưu thay đổi'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ bộ phận con */

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * LỒNG LẠI MỘT DANH SÁCH ĐÃ DUYỆT SÂU — thuần, không React, không luật.
 *
 * ⛔ ĐÂY KHÔNG PHẢI MỘT BẢN THỨ HAI CỦA `xepCay()`. Nó không quyết định ai là con ai: nó đọc
 *   `mucSau` của một danh sách **đã đúng thứ tự** và gắn mỗi nút vào nút gần nhất nông hơn nó
 *   đúng một bậc. Mọi câu hỏi thật — thứ tự, mồ côi, đếm hậu duệ — vẫn nằm trong
 *   `lib/caytaikhoan.ts` và vẫn được mục `[96]` gọi thẳng.
 * ⚠ Cha không tìm thấy (danh sách đã bị `locThu` cắt ngang) ⇒ thả về mức gốc chứ KHÔNG bỏ đi:
 *   *một hàng bị mất là một hàng không ai đi tìm* — cùng luật với `xepCay`.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
type NutVe = { n: NutCay<UserRow>; con: NutVe[] };

function longTheoMuc(ds: readonly NutCay<UserRow>[]): NutVe[] {
  const goc: NutVe[] = [];
  /** `ngan[i]` = nút CUỐI CÙNG gặp ở mức `i` — cha của nút kế tiếp ở mức `i+1`. */
  const ngan: NutVe[] = [];
  for (const n of ds) {
    const nut: NutVe = { n, con: [] };
    const cha = n.mucSau > 0 ? ngan[n.mucSau - 1] : undefined;
    if (cha) cha.con.push(nut); else goc.push(nut);
    ngan[n.mucSau] = nut;
    ngan.length = n.mucSau + 1;   // quên cắt đuôi là gắn cháu vào một bác đã đi qua
  }
  return goc;
}

/**
 * Mã chuỗi cho `Dropdown` thời hạn — xem khối chú thích ở mục ③.
 * ⛔ `null` (VĨNH VIỄN) có mã riêng `'vinh-vien'`: để nó rơi vào `''` là gộp nó với `'giu'`.
 */
const maHan = (v: number | null | 'giu' | 'tuy-chon'): string =>
  v === null ? 'vinh-vien' : typeof v === 'number' ? `thang:${v}` : v;

const docMaHan = (m: string): number | null | 'giu' | 'tuy-chon' => {
  if (m === 'vinh-vien') return null;
  if (m === 'giu' || m === 'tuy-chon') return m;
  return Number(m.slice('thang:'.length));
};

/** Chữ hiện trong ô `Dropdown` — lấy từ chính bảng `GOI_HAN`, không gõ lại lần thứ hai. */
const nhanHan = (v: number | null | 'giu' | 'tuy-chon', isNew: boolean): string => {
  if (v === 'giu') return isNew ? '' : 'Giữ nguyên';
  if (v === 'tuy-chon') return 'Ngày tuỳ ý';
  return GOI_HAN.find((g) => g.thang === v)?.ten ?? '';
};

/**
 * Ô SỐ kiểu biểu tượng-bên-trái của trang Quản trị (mẫu `25`).
 *
 * ⛔ `mau` là một trong BỐN vai đã có nghĩa, không phải một màu tuỳ chọn:
 *     nhan  số nhận diện, KHÔNG mang nghĩa trạng thái   ·  tot   đang hoạt động
 *     tim   tài khoản · thời hạn (luật (c) `thietke.css`) ·  canh  cần chú ý
 *   Vì thế "Khách hàng" dùng TÍM: nó là một con số về TÀI KHOẢN, và tím là màu của thế giới
 *   tài khoản. "Vận hành" dùng cam vì mẫu vẽ thế; nó KHÔNG có nghĩa "đang có việc cần xử lý".
 */
function OSoTK({ icon, mau, nhan, tri, phu }: {
  icon: React.ReactNode;
  mau: 'nhan' | 'tot' | 'tim' | 'canh';
  nhan: string;
  tri: number;
  phu?: string;
}) {
  const s = useStyles();
  const NEN: Record<string, string> = {
    nhan: s.oNhan, tot: s.oTot, tim: s.oTim, canh: s.oCanh,
  };
  return (
    <div className={s.oSo}>
      <span className={`${s.oSoIcon} ${NEN[mau]}`}>{icon}</span>
      <span className="min-w-0">
        <Caption1 className={s.mo}>{nhan}</Caption1>
        <span className="chu-tieude block text-[22px] font-semibold">{tri}</span>
        {phu && <Caption1 className={`${s.mo} block truncate`} title={phu}>{phu}</Caption1>}
      </span>
    </div>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * BẢNG "CHI TIẾT & PHÂN QUYỀN" — màn hình CHỈ-ĐỌC trả lời câu *"tài khoản này làm được gì"*.
 *
 * 🔴 Trước `GD-05`, câu ấy chỉ trả lời được bằng cách **mở hộp Sửa** — tức là mở một màn hình
 *   GHI để ĐỌC, và mỗi lần mở là một lần có thể lỡ tay bấm Lưu. Đó là chỗ hỏng thật, không
 *   phải chuyện bố cục.
 *
 * ⛔ KHÔNG GỌI THÊM API NÀO: mọi thứ ở đây dựng từ `users`/`useRoutes` mà trang đã có. Một
 *   bảng "chi tiết" đi hỏi máy chủ lần nữa là một bảng sẽ nói khác danh sách bên trái đúng
 *   vào lúc hai bên lệch nhịp — và người đọc không có cách nào biết bên nào mới.
 *
 * 🔴 DÒNG "KHÔNG CÓ" LÀ DÒNG ĐẮT NHẤT CỦA BẢNG, và nó gộp HAI lý do:
 *     · tính năng không dành cho HẠNG này (hạng không đổi được ⇒ phải cấp ở tài khoản trên)
 *     · tính năng mà chính tài khoản này chưa được cấp
 *   Ở hộp Sửa (`GD-02b`) hai lý do ấy phải TÁCH, vì ở đó người ta đang đi CẤP và hai lý do
 *   dẫn tới hai hành động khác nhau. Ở đây người ta đang ĐỌC, và câu hỏi chỉ là *"nó không
 *   làm được gì"* — nên gộp là đúng, và nói ra rằng mình đang gộp.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
function ChiTietPhanQuyen({ u, me, tenTheoId, cha, bayGio, soCon, onSua, onDoiMatKhau }: {
  u: UserRow | null;
  me: { id: string; username: string; role?: Role } | null;
  tenTheoId: Map<string, string>;
  /**
   * HÀNG của người tạo, không chỉ cái tên — cần `expiresAt` của cha để nói ra "kẹp theo @cha".
   * ⚠ `null` có HAI nghĩa và cả hai đều im lặng ở dòng Thời hạn: tài khoản gốc (không có cha),
   *   hoặc cha nằm ngoài tầm nhìn. Không nghĩa nào cho phép đoán một cái hạn.
   */
  cha: UserRow | null;
  bayGio: number;
  soCon: number;
  onSua: () => void;
  onDoiMatKhau: () => void;
}) {
  const s = useStyles();
  const routes = useRoutes();
  if (!u) {
    return (
      <Card>
        <CardHeader header={<Text weight="semibold">Chi tiết &amp; phân quyền</Text>} />
        <Caption1 className={s.mo}>Chưa có tài khoản nào để xem.</Caption1>
      </Card>
    );
  }
  const han = docHan(u.expiresAt, bayGio);
  const tenCha = u.createdById ? tenTheoId.get(u.createdById) ?? null : null;
  const laToi = u.id === me?.id;
  /*
   * ⚠ TÊN TUYẾN tra từ `useRoutes()`; `routeAccess` chỉ mang `routeId`. Tra KHÔNG RA ⇒ in mã
   *   id chứ không im lặng bỏ dòng: một tuyến người dùng không nhìn thấy vẫn là một tuyến
   *   tài khoản ấy ĐANG CÓ QUYỀN, và giấu nó đi là báo cáo thiếu quyền.
   */
  const tenTuyen = (id: string) => (routes.data ?? []).find((r) => r.id === id)?.tenDayDu ?? id;
  const coTick = (u.quyen ?? []).filter((k) => (KHOA_TICK as readonly string[]).includes(k));
  const khongCo = KHOA_TICK.filter((k) => !coTick.includes(k));
  return (
    <Card>
      <CardHeader
        header={<Text weight="semibold">Chi tiết &amp; phân quyền</Text>}
        description={<Caption1 className={s.mo}>Tài khoản đang chọn trong danh sách</Caption1>}
      />
      <div className="mb-2 flex items-center gap-2.5">
        <span
          aria-hidden="true"
          className={`${s.anhDaiDien} ${
            u.role === 'SUPER_ADMIN' ? s.avSuper : u.role === 'ADMIN' ? s.avAdmin : s.avManager}`}
        >
          {u.username.charAt(0).toUpperCase()}
        </span>
        <span className="min-w-0">
          <Text weight="semibold">@{u.username}</Text>
          <span className="ml-2 inline-flex flex-wrap gap-1.5">
            <Badge
              appearance="tint" size="small"
              color={u.role === 'ADMIN' ? 'brand' : 'subtle'}
              className={u.role === 'SUPER_ADMIN' ? s.timNen : undefined}
            >
              {tenHang(u.role)}
            </Badge>
            {/*
              * ⚠ BA CA cho người tạo (Luật số 0): `null` = tài khoản GỐC · tra ra tên = in tên
              *   · có id mà tra KHÔNG ra = người tạo nằm ngoài tầm nhìn ⇒ **im lặng**, không
              *   in `@undefined` và cũng không nói "tài khoản gốc" (nói thế là nói SAI).
              */}
            {u.createdById === null && <Badge appearance="tint">tài khoản gốc</Badge>}
            {tenCha && <Badge appearance="tint">tạo bởi @{tenCha}</Badge>}
          </span>
        </span>
      </div>

      <dl className="flex flex-col gap-1">
        <HangCT nhan="Loại tài khoản">{tenHang(u.role)}</HangCT>
        <HangCT nhan="Phạm vi">
          {u.role === 'SUPER_ADMIN'
            ? 'toàn hệ thống'
            : tenCha ? <>trong phạm vi <b>@{tenCha}</b></> : 'trong phạm vi người đã tạo tài khoản này'}
        </HangCT>
        <HangCT nhan="Tuyến được gán">
          {(u.routeAccess ?? []).length === 0
            ? <span className={s.mo}>chưa gán tuyến nào</span>
            : (
              <span className="flex min-w-0 flex-col gap-0.5">
                {(u.routeAccess ?? []).map((r) => (
                  /* ⛔ `block` + `truncate`: một `<span>` inline không cắt được — nó chỉ tràn. */
                  <span key={r.routeId} className="block truncate" title={tenTuyen(r.routeId)}>
                    {tenTuyen(r.routeId)}
                    <span className={`${s.mo} ml-1`}>
                      (xem{r.canControl ? ` · ${NHAN_CO_TUYEN.canControl.toLowerCase()}` : ''}
                      {r.canEditSchedule ? ` · ${NHAN_CO_TUYEN.canEditSchedule.toLowerCase()}` : ''})
                    </span>
                  </span>
                ))}
              </span>
            )}
        </HangCT>
        <HangCT nhan="Tính năng có">
          {coTick.length === 0
            ? <span className={s.mo}>chỉ những thứ tài khoản nào cũng có</span>
            : coTick.map((k) => NHAN_KHOA[k]).join(' · ')}
        </HangCT>
        {/*
          * 🔴 DÒNG NÀY GỘP HAI LÝ DO (xem khối chú thích trên hàm), và nó dùng màu HỔ PHÁCH —
          *   đúng màu "cần người xử lý" của `[95]`, vì đó chính là việc: muốn có thì phải đi
          *   xin, hoặc phải tạo ở một hạng khác.
          */}
        <HangCT nhan="Không có">
          {khongCo.length === 0
            ? <span className={s.mo}>có đủ cả {KHOA_TICK.length} tính năng</span>
            : <span className={s.canhBao}>{khongCo.map((k) => NHAN_KHOA[k]).join(' · ')}</span>}
        </HangCT>
        {/*
          * 🔴 "KẸP THEO @cha" (phiếu chốt) — và đây không phải một lời trang trí: máy chủ chặn
          *   đăng nhập khi **cha hết hạn**, dù con còn hạn hay vĩnh viễn. Một dòng "Vĩnh viễn"
          *   đứng một mình ở đây là một câu SAI về ngày tài khoản này thôi vào được.
          * ⛔ Chỉ in khi CHA CÓ HẠN THẬT (`cha.expiresAt`): cha `null` (gốc / ngoài tầm nhìn)
          *   hoặc cha vĩnh viễn thì KHÔNG có cái kẹp nào — im lặng, không dựng một ngày.
          */}
        <HangCT nhan="Thời hạn">
          <span className="inline-flex flex-col items-end">
            <span className={han.muc === 'het' ? s.hetHan : s.tim}>
              {u.expiresAt ? `${fmtDate(u.expiresAt)} — ${han.cau}` : han.cau}
            </span>
            {cha?.expiresAt && (
              <span className={`${s.mo} text-xs`}>
                kẹp theo @{cha.username}: {fmtDate(cha.expiresAt)}
              </span>
            )}
          </span>
        </HangCT>
        <HangCT nhan="Lần đăng nhập cuối">
          {u.lastLoginAt
            ? <>{fmtDateTime(u.lastLoginAt)} <span className={s.mo}>· {fmtAgo(u.lastLoginAt)}</span></>
            : <span className={s.mo}>chưa đăng nhập lần nào</span>}
        </HangCT>
        <HangCT nhan="Trạng thái">
          <Badge color={mauTrangThai(u.isActive)} appearance="tint">
            {u.isActive ? 'Đang dùng' : 'Đã khoá'}
          </Badge>
          {soCon > 0 && <span className={`${s.mo} ml-2`}>· {soCon} tài khoản phụ</span>}
        </HangCT>
      </dl>

      {/*
        * ⚠ HAI NÚT, và nút thứ hai CHỈ HIỆN VỚI CHÍNH MÌNH (phiếu chốt). Nó không mở một màn
        *   hình khác: cùng hộp `GD-02b`, chỉ cuộn thẳng tới ô mật khẩu — đúng việc cái 🔑 ở
        *   danh sách đang làm. Một lối thứ hai tới cùng một chỗ thì không có gì để lệch nhau.
        */}
      <div className="mt-2 flex flex-wrap gap-2">
        <Button appearance="secondary" icon={<Edit20Regular />} onClick={onSua}>
          {laToi ? 'Chỉnh sửa thông tin của tôi' : 'Chỉnh sửa thông tin'}
        </Button>
        {laToi && (
          <Button appearance="secondary" icon={<Key20Regular />} onClick={onDoiMatKhau}>
            Đổi mật khẩu của tôi
          </Button>
        )}
      </div>
    </Card>
  );
}

/** Một hàng của bảng chi tiết — nhãn nhỏ hoa bên trái, giá trị bên phải. */
function HangCT({ nhan, children }: { nhan: string; children: React.ReactNode }) {
  const s = useStyles();
  return (
    <div className={s.hangCT}>
      <dt className={`${s.mo} shrink-0 text-xs uppercase tracking-wide`}>{nhan}</dt>
      {/* ⚠ `overflow-hidden` là vế thứ hai của `min-w-0`: cho co lại RỒI mới cắt được. */}
      <dd className="m-0 min-w-0 overflow-hidden text-right text-[12.5px]">{children}</dd>
    </div>
  );
}

/** Một mục có SỐ THỨ TỰ — thứ tự là một phần của yêu cầu, nên nó phải nhìn thấy được. */
function Muc({ so, ten, children }: { so: string; ten: string; children: React.ReactNode }) {
  const s = useStyles();
  return (
    <section className={s.muc}>
      <h3 className="mb-2 flex items-center gap-2">
        <span className={`${s.soMuc} inline-flex h-5 w-5 items-center justify-center text-xs`}>{so}</span>
        <Text weight="semibold">{ten}</Text>
      </h3>
      <div className="flex flex-col gap-3">{children}</div>
    </section>
  );
}

/**
 * Khoá NGƯỜI TẠO được phép cấp lại. `SUPER_ADMIN` có `quyen = []` mà qua mọi cổng ⇒ nó thấy
 * CẢ NĂM; đọc `[]` của nó thành "không có gì" là đúng dữ liệu mà sai nghĩa.
 */
function tickChoPhepCua(me: { quyen?: string[] } | null, isSuper: boolean): string[] {
  if (isSuper) return [...KHOA_TICK];
  return (me?.quyen ?? []).filter((k) => (KHOA_TICK as readonly string[]).includes(k));
}

/*
 * TK-16 B4 — SỨC KHOẺ MÁY CHỦ, đọc `/health` mỗi 30 s. Hai con số KHÁC nhau và phải đứng cạnh nhau:
 *   · "Socket đang mở" (`devicesOnline`) — TK-05b đo được: báo đủ N ngay cả khi 0 tủ được phục vụ;
 *   · "Tủ đang gửi số liệu" (`tuDangGui`, khung số liệu trong 90 s) — con số người trực cần.
 * `meterBoRoi` > 0 = máy chủ đã BỎ khung đo đếm để giữ đăng nhập/lệnh (P2) sống — có đếm, không im.
 */
const useSucKhoeStyles = makeStyles({
  khung: {
    borderTopWidth: '1px', borderRightWidth: '1px', borderBottomWidth: '1px', borderLeftWidth: '1px',
    borderTopStyle: 'solid', borderRightStyle: 'solid', borderBottomStyle: 'solid', borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2, borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2, borderLeftColor: tokens.colorNeutralStroke2,
    borderRadius: tokens.borderRadiusLarge, padding: '12px 16px',
  },
  phu: { color: tokens.colorNeutralForeground3 },
  canh: { color: tokens.colorStatusWarningForeground1 },
});

/**
 * TK-19 — "ĐÃ NẠP HẾT CHƯA" là MỘT câu hỏi trả lời được ở đây, không phải cắm dây serial từng tủ.
 * ⚠ Cột "Khai gần nhất / xa nhất": tủ offline 3 tháng giữ bản cũ ≠ tủ vừa khai bản cũ 2 phút trước —
 *   không phân biệt thì người đi tìm tủ sẽ ra đường tìm một tủ đã hỏng từ lâu.
 */
function TheBanFirmware() {
  const tk = useThongKeFirmware();
  const s = useSucKhoeStyles();
  if (tk.isError) return <ErrorBox error={tk.error} onRetry={() => void tk.refetch()} />;
  const d = tk.data;
  return (
    <div className={`text-sm ${s.khung}`} data-o="ban-firmware">
      <p className="mb-1 font-semibold">Bản firmware trên tủ</p>
      {/*
        * TK-24 — đo trên máy thật 375 px (09/10): cột cố định 80+96+160+160 = 496 px trong khung ~340 ⇒ cột "Bản"
        *   còn 0 px, tiêu đề đè nhau, và cả TRANG tràn ngang 150 px (`scrollWidth 525`). Bọc khung cuộn riêng +
        *   sàn 600 px (Bản ≥ 104 px) — cuộn trong khung, không kéo lệch trang.
        */}
      {!d ? <p className={`text-xs ${s.phu}`}>Đang đếm…</p> : (
        <div className="tbl-wrap cuon-doc">
        <table className="tbl tbl-cuon min-w-[600px] table-fixed tabular-nums">
          <colgroup><col /><col className="w-20" /><col className="w-24" /><col className="w-40" /><col className="w-40" /></colgroup>
          <thead><tr className="text-left text-xs uppercase">
            <th>Bản</th><th>Số tủ</th><th>Đang nối</th><th>Khai gần nhất</th><th>Khai xa nhất</th>
          </tr></thead>
          <tbody>
            {d.nhom.map((n) => (
              <tr key={`${n.git}|${n.dirty}`} title={n.tu.map((t) => t.hardwareId).join(', ')}>
                <td className="truncate">
                  {n.git == null ? <i>chưa biết</i> : <b>{n.git}</b>}
                  {n.dirty ? <span className={`ml-1 ${s.canh}`}>dirty</span> : null}
                </td>
                <td>{n.soTu}</td><td>{n.soOnline}</td>
                <td>{n.fwLucMoiNhat ? fmtAgo(n.fwLucMoiNhat) : '—'}</td>
                <td>{n.fwLucCuNhat ? fmtAgo(n.fwLucCuNhat) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      {d && <p className={`mt-1 text-xs ${s.phu}`} title="Chưa khai bản = tủ chưa nối lại từ khi máy chủ biết đọc bản firmware">{d.tongTu} tủ · {d.soChuaBiet} chưa khai bản</p>}
    </div>
  );
}

function TheSucKhoeMayChu() {
  const sk = useSucKhoeMayChu();
  const s = useSucKhoeStyles();
  if (sk.isError) return <ErrorBox error={sk.error} onRetry={() => void sk.refetch()} />;
  const d = sk.data;
  const o = (x: number | undefined) => (x === undefined ? 'máy chủ chưa khai' : x.toLocaleString('vi-VN'));
  return (
    <div className={`text-sm ${s.khung}`} data-o="suc-khoe-may-chu">
      <p className="mb-1 font-semibold">Sức khoẻ máy chủ</p>
      {!d ? <p className={`text-xs ${s.phu}`}>Đang hỏi /health…</p> : (
        <div className="flex flex-wrap gap-x-6 gap-y-1 tabular-nums">
          <span>Socket đang mở: <b>{o(d.devicesOnline)}</b></span>
          <span>Tủ đang gửi số liệu (90 s): <b>{o(d.tuDangGui)}</b></span>
          <span>Hàng đợi đo đếm: <b>{o(d.meterHangDoi)}</b></span>
          <span className={d.meterBoRoi ? s.canh : undefined}>Khung đo đếm đã bỏ: <b>{o(d.meterBoRoi)}</b></span>
          <span>Ghi đo đếm hỏng: <b>{o(d.meterGhiHong)}</b></span>
        </div>
      )}
    </div>
  );
}
